import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import FactoryFloorView from "./FactoryFloorView";
import FactoryFloor1Table from "./FactoryFloor1Table";
import LoginScreen from "./LoginScreen";
import AgentErrorBoundary from "./AgenticAI/ErrorBoundary";
import EnhancedMarkdownRenderer from "./AgenticAI/EnhancedMarkdownRenderer";
import { isA3Message } from "./AgenticAI/a3DetectionUtils";
import ConversationSidebar from "./AgenticAI/ConversationSidebar";
import { msalInstance, getActiveAccount, logout } from "./auth/msal";
import { EventType } from "@azure/msal-browser";
import { getAccessToken } from "./api/authClient";

// Constants
const loadingMessages = [
  { text: "Fetching factory data...", duration: 2200 },
  { text: "Analyzing metrics...", duration: 2500 },
  { text: "Processing insights...", duration: 2000 },
  { text: "Reasoning through patterns...", duration: 2800 },
  { text: "Generating response...", duration: 2300 }
];

function App() {
  const bypassLogin = process.env.REACT_APP_BYPASS_LOGIN === "true";
  
  // PPE Feature Toggles
  const showFactoryFloor = process.env.REACT_APP_SHOW_FACTORY_FLOOR !== "false";
  const showDailyManagement = process.env.REACT_APP_SHOW_DAILY_MANAGEMENT !== "false";
  const showAgentChat = process.env.REACT_APP_SHOW_AGENT_CHAT !== "false";
  const isPPE = process.env.REACT_APP_ENVIRONMENT === "PPE";
  
  const [account, setAccount] = useState(bypassLogin ? { username: "dev-user@example.com" } : null);
  const [activePage, setActivePage] = useState(isPPE ? "Agentic AI" : "Factory Floor");
  const [reportHeaders, setReportHeaders] = useState([]);
  const [reportRows, setReportRows] = useState([]);
  const [valueStreamReports, setValueStreamReports] = useState({});
  const [hourlyRowsByProcess, setHourlyRowsByProcess] = useState({});
  const [reportStatus, setReportStatus] = useState("idle");
  const [reportError, setReportError] = useState("");
  const [reportStartDate, setReportStartDate] = useState("2025-01-01");
  const [reportEndDate, setReportEndDate] = useState("2025-06-30");
  const [reportSort, setReportSort] = useState("date:asc");
  const [reportPlant, setReportPlant] = useState("Atlanta");
  const [reportValueStream, setReportValueStream] = useState("all");
  const [reportMachine, setReportMachine] = useState("all");
  const [trendGranularity, setTrendGranularity] = useState("day");
  const [a3ValueStream, setA3ValueStream] = useState("all");
  const [a3Department, setA3Department] = useState("all");
  const dataSourceRef = useRef(null);
  
  // Agent chat state
  const [agentMessages, setAgentMessages] = useState([]);
  const [agentInput, setAgentInput] = useState("");
  const [agentLoading, setAgentLoading] = useState(false);
  const [agentError, setAgentError] = useState("");
  const [agentConfig, setAgentConfig] = useState(null);
  const [agentThreadId, setAgentThreadId] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [loadingMessageIndex, setLoadingMessageIndex] = useState(0);
  const [generatingA3Idx, setGeneratingA3Idx] = useState(null);
  const chatContainerRef = useRef(null);
  const textareaRef = useRef(null);
  const sidebarZoneRef = useRef(null);

  // Close history sidebar when clicking outside the sidebar zone
  useEffect(() => {
    if (!sidebarOpen) return;
    function handleMouseDown(e) {
      if (sidebarZoneRef.current && !sidebarZoneRef.current.contains(e.target)) {
        setSidebarOpen(false);
      }
    }
    document.addEventListener('mousedown', handleMouseDown);
    return () => document.removeEventListener('mousedown', handleMouseDown);
  }, [sidebarOpen]);

  // Cycle through loading messages with variable timing, stop at last message
  useEffect(() => {
    if (!agentLoading) {
      setLoadingMessageIndex(0);
      return;
    }
    
    // Don't advance if we're at the last message
    if (loadingMessageIndex >= loadingMessages.length - 1) {
      return;
    }
    
    const scheduleNext = (index) => {
      const nextIndex = index + 1;
      const duration = loadingMessages[index].duration;
      return setTimeout(() => {
        if (nextIndex < loadingMessages.length) {
          setLoadingMessageIndex(nextIndex);
        }
      }, duration);
    };
    
    const timeout = scheduleNext(loadingMessageIndex);
    return () => clearTimeout(timeout);
  }, [agentLoading, loadingMessageIndex]);

  // Auto-scroll to bottom when messages update
  useEffect(() => {
    if (chatContainerRef.current) {
      chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
    }
  }, [agentMessages, agentLoading]);

  // Auto-scroll to bottom when switching to Agentic AI tab
  useEffect(() => {
    if (activePage === "Agentic AI" && chatContainerRef.current) {
      setTimeout(() => {
        if (chatContainerRef.current) {
          chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
        }
      }, 100);
    }
  }, [activePage]);
  
  // Reset textarea height when input is cleared
  useEffect(() => {
    if (agentInput === "" && textareaRef.current) {
      textareaRef.current.style.height = "auto";
    }
  }, [agentInput]);
  
  const forceLogin = typeof window !== "undefined" && window.location.pathname.toLowerCase().includes("/login");

  const getDataSourceConfig = useCallback(async () => {
    if (dataSourceRef.current) return dataSourceRef.current;
    try {
      const response = await fetch("/data-source.json", { cache: "no-store" });
      if (!response.ok) return null;
      const data = await response.json();
      if (!data?.containerUrl && !data?.blobProxyPath && !data?.useManagedIdentity) return null;
      dataSourceRef.current = data;
      return data;
    } catch (error) {
      console.warn("Failed to load data source config", error);
      return null;
    }
  }, []);

  const buildBlobUrl = useCallback((config, folder, fileName) => {
    if (!config) return null;
    const normalizedFolder = folder ? folder.replace(/^\/+|\/+$/g, "") : "";
    const blobPath = normalizedFolder ? `${normalizedFolder}/${fileName}` : fileName;

    if (config.useManagedIdentity || config.blobProxyPath) {
      const hostname = typeof window !== "undefined" ? window.location.hostname : "";
      const isLocalhost = hostname === "localhost" || hostname === "127.0.0.1";
      const defaultBlobProxyPath = "https://stratalign-factory-api.azurewebsites.net/api/blob";
      const basePath = isLocalhost ? "/api/blob" : (config.blobProxyPath || defaultBlobProxyPath);
      const baseUrl = basePath.startsWith("http") ? basePath.replace(/\/$/, "") : basePath;
      const params = new URLSearchParams({ path: blobPath });
      return `${baseUrl}?${params.toString()}`;
    }

    const sasToken = (config.sasToken || "").replace(/^\?/, "");
    if (!config.containerUrl || !sasToken) return null;
    const encodedName = blobPath.split("/").map(encodeURIComponent).join("/");
    return `${config.containerUrl.replace(/\/$/, "")}/${encodedName}?${sasToken}`;
  }, []);

  const getNumber = useCallback((value) => {
    if (value === null || value === undefined || value === "") return NaN;
    const parsed = Number(value);
    return Number.isNaN(parsed) ? NaN : parsed;
  }, []);

  const parseDateValue = useCallback((value) => {
    if (!value) return NaN;
    const normalized = String(value).trim();
    if (!normalized) return NaN;
    const withTime = normalized.length === 10 ? `${normalized}T00:00:00` : normalized;
    const timestamp = Date.parse(withTime);
    return Number.isNaN(timestamp) ? NaN : timestamp;
  }, []);

  const a3Records = useMemo(
    () => [
      {
        a3Details: "Reduce changeover time on QCS line",
        owner: "M. Patel",
        status: "In progress",
        expectedOutput: "15% cycle time reduction",
        valueStream: "QCS",
        department: "Operations"
      },
      {
        a3Details: "Scrap reduction at Foam Kitting",
        owner: "L. Chen",
        status: "Planned",
        expectedOutput: "Scrap rate below 2%",
        valueStream: "Foam Kitting",
        department: "Quality"
      },
      {
        a3Details: "Staging flow balance review",
        owner: "A. Gomez",
        status: "In progress",
        expectedOutput: "Reduce queue time by 20%",
        valueStream: "Staging",
        department: "Operations"
      },
      {
        a3Details: "Mat Build uptime Kaizen",
        owner: "S. Rivera",
        status: "Complete",
        expectedOutput: "OEE +3 points",
        valueStream: "Mat Build",
        department: "Maintenance"
      },
      {
        a3Details: "Packaging & Shipping defect containment",
        owner: "R. Singh",
        status: "Planned",
        expectedOutput: "Customer complaints < 0.5%",
        valueStream: "Packaging & Shipping",
        department: "Quality"
      }
    ],
    []
  );

  const a3ValueStreamOptions = useMemo(
    () => Array.from(new Set(a3Records.map((record) => record.valueStream))),
    [a3Records]
  );

  const a3DepartmentOptions = useMemo(
    () => Array.from(new Set(a3Records.map((record) => record.department))),
    [a3Records]
  );

  const filteredA3Records = useMemo(
    () =>
      a3Records.filter((record) => {
        const valueStreamMatch = a3ValueStream === "all" || record.valueStream === a3ValueStream;
        const departmentMatch = a3Department === "all" || record.department === a3Department;
        return valueStreamMatch && departmentMatch;
      }),
    [a3Records, a3ValueStream, a3Department]
  );

  useEffect(() => {
    if (bypassLogin) {
      return; // Skip MSAL initialization in bypass mode
    }
    const callbackId = msalInstance.addEventCallback((event) => {
      if (event.eventType === EventType.LOGIN_SUCCESS) {
        const acc = getActiveAccount();
        setAccount(acc);
      }
    });
    msalInstance
      .handleRedirectPromise()
      .then(() => {
        const acc = getActiveAccount();
        setAccount(acc);
      })
      .catch(() => undefined);
    return () => {
      if (callbackId) msalInstance.removeEventCallback(callbackId);
    };
  }, [bypassLogin]);

  // Load conversation from localStorage on mount
  useEffect(() => {
    try {
      const savedThreadId = localStorage.getItem('agentThreadId');
      const savedMessages = localStorage.getItem('agentMessages');
      
      if (savedThreadId) {
        setAgentThreadId(savedThreadId);
        console.log('[localStorage] Loaded thread_id:', savedThreadId);
      }
      
      if (savedMessages) {
        const parsed = JSON.parse(savedMessages);
        setAgentMessages(parsed);
        console.log('[localStorage] Loaded', parsed.length, 'messages');
      }
    } catch (error) {
      console.error('[localStorage] Failed to load conversation:', error);
    }
  }, []); // Run once on mount

  // Save conversation to localStorage when it changes
  useEffect(() => {
    try {
      if (agentThreadId) {
        localStorage.setItem('agentThreadId', agentThreadId);
      } else {
        localStorage.removeItem('agentThreadId');
      }
      
      if (agentMessages.length > 0) {
        localStorage.setItem('agentMessages', JSON.stringify(agentMessages));
        console.log('[localStorage] Saved', agentMessages.length, 'messages');
      } else {
        localStorage.removeItem('agentMessages');
      }
    } catch (error) {
      console.error('[localStorage] Failed to save conversation:', error);
    }
  }, [agentMessages, agentThreadId]);

  useEffect(() => {
    if (bypassLogin || account) return;
    fetch("/.auth/me")
      .then((response) => (response.ok ? response.json() : null))
      .then((data) => {
        const principal = data && data.clientPrincipal;
        if (principal) {
          setAccount({
            username: principal.userDetails || principal.identityProvider || "Authenticated User",
            provider: principal.identityProvider,
          });
        }
      })
      .catch(() => undefined);
  }, [account, bypassLogin]);

  const valueStreamProcesses = useMemo(
    () => ({
      QCS: 1,
      Coiling: 2,
      "Foam Kitting": 3,
      Staging: 4,
      "Mat Build": 5,
      Closing: 6,
      "Packaging & Shipping": 7,
    }),
    [],
  );

  useEffect(() => {
    if (reportStatus !== "idle") return;
    setReportStatus("loading");

    const parseCsv = (text) => {
      const lines = text.trim().split(/\r?\n/).filter(Boolean);
      if (!lines.length) {
        throw new Error("Report data is empty");
      }
      const headers = lines[0].split(",").map((value) => value.trim());
      const rows = lines.slice(1).map((line) => {
        const values = line.split(",");
        return headers.reduce((acc, header, index) => {
          acc[header] = values[index] ?? "";
          return acc;
        }, {});
      });
      return { headers, rows };
    };

    const loadFromBlob = async () => {
      const config = await getDataSourceConfig();
      const hostname = typeof window !== "undefined" ? window.location.hostname : "";
      const isLocalhost = hostname === "localhost" || hostname === "127.0.0.1";
      const preferBlob =
        config?.preferBlob !== false && (!isLocalhost || config?.preferBlobOnLocalhost === true);
      const useProxy = config?.useManagedIdentity || config?.blobProxyPath;
      const canUseBlob = !!(preferBlob && config && (useProxy || (config.containerUrl && config.sasToken)));
      const dailyFolder = config.dailyFolder || "";
      const plantDailyFolder =
        config.plantDailyFolder || "startalignllc_data/plant1/plant_daily_data/raw";
      const plantDailyFile = config.plantDailyFile || "plant1.csv";

      const fetchFirstAvailable = async (urls) => {
        for (const url of urls) {
          if (!url) continue;
          try {
            const response = await fetch(url);
            if (response.ok) {
              const text = await response.text();
              console.log(`✓ Successfully loaded data from: ${url}`);
              return text;
            } else {
              console.warn(`✗ Failed to load from ${url}: ${response.status} ${response.statusText}`);
            }
          } catch (error) {
            console.warn(`✗ Error loading from ${url}:`, error.message);
          }
        }
        console.error("All data sources failed for URLs:", urls);
        return "";
      };

      const plantUrl = buildBlobUrl(config, plantDailyFolder, plantDailyFile);
      const plantFallbackUrl = "/data/plant1.csv";
      let blobAvailable = canUseBlob;
      let plantTextFromBlob = "";

      if (blobAvailable && plantUrl) {
        try {
          const response = await fetch(plantUrl);
          if (response.ok) {
            plantTextFromBlob = await response.text();
            console.log(`✓ Successfully loaded data from: ${plantUrl}`);
          } else {
            blobAvailable = false;
            console.warn(`Blob source unavailable (${response.status}). Using local CSV fallback.`);
          }
        } catch (error) {
          blobAvailable = false;
          console.warn("Blob source unavailable. Using local CSV fallback.", error?.message || error);
        }
      }

      const plantText = plantTextFromBlob || (await fetchFirstAvailable([plantFallbackUrl]));
      if (!plantText) throw new Error("Failed to load plant-level data");
      const parsed = parseCsv(plantText);
      const plantData = { headers: parsed.headers, rows: parsed.rows };

      const streamEntries = await Promise.allSettled(
        Object.entries(valueStreamProcesses).map(async ([stream, processId]) => {
          const fileName = `process_${processId}.csv`;
          const url = blobAvailable ? buildBlobUrl(config, dailyFolder, fileName) : "";
          const localFallbackUrl = `/data/process_${processId}.csv`;
          const text = await fetchFirstAvailable([url, localFallbackUrl]);
          if (!text) throw new Error(`Failed to load ${stream} report data`);
          return { stream, data: parseCsv(text) };
        }),
      );

      const streamMap = streamEntries.reduce((acc, result) => {
        if (result.status === "fulfilled") {
          acc[result.value.stream] = result.value.data;
        } else {
          console.warn("Process-level blob load failed", result.reason);
        }
        return acc;
      }, {});

      return { plantData, streamMap };
    };

    Promise.resolve()
      .then(async () => await loadFromBlob())
      .then(({ plantData, streamMap }) => {
        setReportHeaders(plantData.headers);
        setReportRows(plantData.rows);
        setValueStreamReports(streamMap);
        setReportStatus("ready");
      })
      .catch((error) => {
        setReportError(error.message || "Unable to load report data");
        setReportStatus("error");
      });
  }, [reportStatus, buildBlobUrl, getDataSourceConfig, getNumber, valueStreamProcesses]);

  useEffect(() => {
    const loadHourly = async () => {
      const normalizeKey = (key) => String(key).toLowerCase().replace(/[^a-z0-9]/g, "");
      const mapKey = (key) => {
        const normalized = normalizeKey(key);
        if (normalized === "date") return "date";
        if (normalized === "hourstart" || normalized === "starttime") return "hour_start";
        if (normalized === "hourend" || normalized === "endtime") return "hour_end";
        if (normalized === "machine" || normalized === "machinename") return "machine";
        if (normalized === "process" || normalized === "processname") return "process";
        if (normalized === "plannedunits" || normalized === "planned") return "planned_units";
        if (normalized === "actualunits" || normalized === "actual") return "actual_units";
        if (normalized === "defectstotal" || normalized === "defects") return "defects_total";
        if (normalized === "downtimeoccurrence" || normalized === "downtimeoccurrences") return "downtime_occurrence";
        if (normalized === "downtimeminutes" || normalized === "downtime") return "downtime_minutes";
        if (/^d[1-5]$/.test(normalized)) return normalized;
        return key;
      };

      const parseHourly = (text) => {
        if (!text) return [];
        const lines = text.trim().split(/\r?\n/).filter(Boolean);
        if (!lines.length) return [];
        const headers = lines[0].split(",").map((value) => value.trim());
        return lines.slice(1).map((line) => {
          const values = line.split(",");
          return headers.reduce((acc, header, index) => {
            acc[mapKey(header)] = values[index] ?? "";
            return acc;
          }, {});
        });
      };

      const processEntries = Object.entries(valueStreamProcesses);
      const results = {};

      try {
        const config = await getDataSourceConfig();
        const hostname = typeof window !== "undefined" ? window.location.hostname : "";
        const isLocalhost = hostname === "localhost" || hostname === "127.0.0.1";
        const preferBlob =
          config?.preferBlob !== false && (!isLocalhost || config?.preferBlobOnLocalhost === true);
        const useProxy = config?.useManagedIdentity || config?.blobProxyPath;
        const canUseBlob = preferBlob && config && (useProxy || (config.containerUrl && config.sasToken));
        const hourlyFolder = config?.hourlyFolder || "";
        let blobAvailable = !!canUseBlob;

        const fetchFirstAvailableHourly = async (urls) => {
          for (const url of urls) {
            if (!url) continue;
            try {
              const response = await fetch(url);
              if (response.ok) {
                const text = await response.text();
                console.log(`✓ Successfully loaded hourly data from: ${url}`);
                return text;
              } else {
                console.warn(`✗ Failed to load hourly from ${url}: ${response.status} ${response.statusText}`);
              }
            } catch (error) {
              console.warn(`✗ Error loading hourly from ${url}:`, error.message);
            }
          }
          return "";
        };

        if (blobAvailable) {
          const probeUrl = buildBlobUrl(config, hourlyFolder, "process_1.csv");
          try {
            const response = await fetch(probeUrl);
            if (!response.ok) {
              blobAvailable = false;
              console.warn(`Hourly blob source unavailable (${response.status}). Using local CSV fallback.`);
            }
          } catch (error) {
            blobAvailable = false;
            console.warn("Hourly blob source unavailable. Using local CSV fallback.", error?.message || error);
          }
        }

        await Promise.all(
          processEntries.map(async ([, processId]) => {
            const fileName = `process_${processId}.csv`;
            const blobUrl = blobAvailable ? buildBlobUrl(config, hourlyFolder, fileName) : null;
            const localFallbackUrl = `/data/HourlyData/${fileName}`;
            
            const text = await fetchFirstAvailableHourly([blobUrl, localFallbackUrl]);
            const rows = parseHourly(text);
            if (rows.length) {
              results[processId] = rows;
            }
          }),
        );
      } catch (error) {
        console.warn("Failed to load hourly data", error);
      }

      setHourlyRowsByProcess(results);
    };

    loadHourly();
  }, [buildBlobUrl, getDataSourceConfig, valueStreamProcesses]);

  useEffect(() => {
    const loadAgentConfig = async () => {
      try {
        const response = await fetch("/agent-config.json", { cache: "no-store" });
        if (!response.ok) return;
        const data = await response.json();
        setAgentConfig(data);
      } catch (error) {
        console.warn("Failed to load agent config", error);
      }
    };

    loadAgentConfig();
  }, []);

  const isValueStream = reportValueStream !== "all";
  const isMachineLevelValueStream = isValueStream && reportMachine !== "all";

  const formatTrendDateKey = useCallback((rawDate, granularity) => {
    if (!rawDate) return "";
    if (granularity === "day") return String(rawDate);
    const timestamp = parseDateValue(rawDate);
    if (Number.isNaN(timestamp)) return "";
    const parsed = new Date(timestamp);
    if (granularity === "month") {
      return `${parsed.getFullYear()}-${String(parsed.getMonth() + 1).padStart(2, "0")}`;
    }
    const dayIndex = (parsed.getDay() + 6) % 7;
    const weekStart = new Date(parsed);
    weekStart.setDate(parsed.getDate() - dayIndex);
    return `${weekStart.getFullYear()}-${String(weekStart.getMonth() + 1).padStart(2, "0")}-${String(
      weekStart.getDate(),
    ).padStart(2, "0")}`;
  }, [parseDateValue]);

  const buildChartMetrics = useCallback(
    (row) => ({
      safetyActual: getNumber(row.safety_incidents_actual),
      safetyGoal: getNumber(isValueStream ? row.safety_incidents_target : row.safety_incidents_goal),
      qualityActual: getNumber(
        isValueStream ? row.quality_fpy_actual_pct : row.quality_customer_defects_actual,
      ),
      qualityGoal: getNumber(
        isValueStream ? row.quality_fpy_target_pct : row.quality_customer_defects_goal,
      ),
      otifActual: getNumber(
        isValueStream ? row.delivery_hr_by_hr_actual_pct : row.delivery_otif_actual_pct,
      ),
      otifGoal: getNumber(
        isValueStream ? row.delivery_hr_by_hr_target_pct : row.delivery_otif_goal_pct,
      ),
      productionActual: getNumber(
        isMachineLevelValueStream ? row.pph_actual : row.cost_productivity_actual_pct,
      ),
      productionGoal: getNumber(
        isMachineLevelValueStream
          ? row.pph_goal
          : isValueStream
            ? row.cost_productivity_target_pct
            : row.cost_productivity_goal_pct,
      ),
      overtimeActual: getNumber(isValueStream ? row.downtime_minutes : row.cost_overtime_actual_pct),
      overtimeGoal: getNumber(isValueStream ? 50 : row.cost_overtime_goal_pct),
      crossTrainingActual: getNumber(row.people_cross_training_actual_pct),
      crossTrainingGoal: getNumber(
        isValueStream ? row.people_cross_training_target_pct : row.people_cross_training_goal_pct,
      ),
      absenteeismActual: getNumber(isValueStream ? row.hours_missed_goal : row.people_absenteeism_actual_pct),
      absenteeismGoal: getNumber(isValueStream ? 0 : row.people_absenteeism_goal_pct),
    }),
    [getNumber, isMachineLevelValueStream, isValueStream],
  );

  const plantKey = useMemo(
    () => reportHeaders.find((header) => /plant/i.test(header)) || "",
    [reportHeaders],
  );

  const valueStreamKey = useMemo(
    () => reportHeaders.find((header) => /value\s*stream|value_stream/i.test(header)) || "",
    [reportHeaders],
  );

  const machineKey = useMemo(
    () => reportHeaders.find((header) => /machine/i.test(header)) || "",
    [reportHeaders],
  );

  const valueStreamOptions = useMemo(
    () => ["QCS", "Coiling", "Foam Kitting", "Staging", "Mat Build", "Closing", "Packaging & Shipping"],
    [],
  );
  const activeHourlyRows = useMemo(() => {
    if (!isValueStream) return [];
    const processId = valueStreamProcesses[reportValueStream];
    return hourlyRowsByProcess[processId] || [];
  }, [hourlyRowsByProcess, isValueStream, reportValueStream, valueStreamProcesses]);

  const machineOptions = useMemo(() => {
    if (!isValueStream) return [];
    return Array.from(new Set(activeHourlyRows.map((row) => row.machine).filter(Boolean))).sort();
  }, [activeHourlyRows, isValueStream]);

  const normalizePlant = useCallback((value) => {
    const raw = String(value ?? "").trim().toLowerCase();
    if (!raw) return "";
    if (raw === "plant 1" || raw === "plant1") return "atlanta";
    if (raw === "plant 2" || raw === "plant2") return "las vegas";
    if (raw === "plant 3" || raw === "plant3") return "boston";
    if (raw === "plant 4" || raw === "plant4") return "bentonville";
    if (raw.includes("atlanta")) return "atlanta";
    if (raw.includes("las vegas") || raw.includes("vegas")) return "las vegas";
    if (raw.includes("boston")) return "boston";
    if (raw.includes("bentonville")) return "bentonville";
    return raw;
  }, []);

  const getRowPlantNormalized = (row) => {
    if (!plantKey) return "";
    return normalizePlant(row[plantKey]);
  };

  const getRowValueStream = (row) => {
    if (valueStreamKey) return String(row[valueStreamKey] ?? "");
    return "";
  };

  const getRowMachine = (row) => {
    if (machineKey) return String(row[machineKey] ?? "");
    return "";
  };

  const activeReport = useMemo(
    () => (isValueStream ? valueStreamReports[reportValueStream] : { headers: reportHeaders, rows: reportRows }),
    [isValueStream, reportHeaders, reportRows, reportValueStream, valueStreamReports],
  );
  const activeReportHeaders = useMemo(() => activeReport?.headers ?? [], [activeReport]);
  const activeReportRows = useMemo(() => activeReport?.rows ?? [], [activeReport]);
  const plantOptions = useMemo(
    () => ["Atlanta", "Las Vegas", "Boston", "Bentonville"],
    [],
  );
  const sortOptions = useMemo(
    () =>
      (isValueStream
        ? [
            { value: "date:asc", label: "Date (oldest first)" },
            { value: "date:desc", label: "Date (newest first)" },
            { value: "delivery_hr_by_hr_actual_pct:desc", label: "Delivery actual (high → low)" },
            { value: "cost_productivity_actual_pct:desc", label: "Productivity actual (high → low)" },
            { value: "quality_fpy_actual_pct:desc", label: "FPY actual (high → low)" },
            { value: "downtime_minutes:asc", label: "Downtime minutes (low → high)" },
          ]
        : [
            { value: "date:asc", label: "Date (oldest first)" },
            { value: "date:desc", label: "Date (newest first)" },
            { value: "delivery_otif_actual_pct:desc", label: "OTIF actual (high → low)" },
            { value: "cost_productivity_actual_pct:desc", label: "Productivity actual (high → low)" },
            { value: "cost_overtime_actual_pct:asc", label: "Overtime actual (low → high)" },
          ]),
    [isValueStream],
  );

  useEffect(() => {
    if (!sortOptions.some((option) => option.value === reportSort)) {
      setReportSort(sortOptions[0]?.value || "date:asc");
    }
  }, [reportSort, sortOptions]);

  useEffect(() => {
    if (isValueStream) {
      if (reportMachine !== "all" && !machineOptions.includes(reportMachine)) {
        setReportMachine("all");
      }
      return;
    }

    if (reportMachine !== "all") {
      setReportMachine("all");
    }
  }, [isValueStream, machineOptions, reportMachine]);

  const filteredHourlyRows = useMemo(() => {
    if (!isValueStream) return [];
    return activeHourlyRows.filter((row) => {
      if (!reportStartDate && !reportEndDate) return true;
      const rowDate = parseDateValue(row.date);
      if (Number.isNaN(rowDate)) return true;
      const startDate = reportStartDate ? parseDateValue(reportStartDate) : NaN;
      const endDate = reportEndDate ? parseDateValue(reportEndDate) : NaN;
      if (!Number.isNaN(startDate) && rowDate < startDate) return false;
      if (!Number.isNaN(endDate) && rowDate > endDate) return false;
      return true;
    });
  }, [activeHourlyRows, isValueStream, parseDateValue, reportEndDate, reportStartDate]);

  const paretoSourceRows = useMemo(() => {
    if (!isValueStream) return [];
    if (reportMachine === "all") return filteredHourlyRows;
    return filteredHourlyRows.filter((row) => row.machine === reportMachine);
  }, [filteredHourlyRows, isValueStream, reportMachine]);

  const selectedMachineHourlyRows = useMemo(() => {
    if (!isValueStream) return [];
    if (reportMachine === "all") return [];

    const grouped = filteredHourlyRows
      .filter((row) => row.machine === reportMachine)
      .reduce((acc, row) => {
        const key = String(row.hour_start ?? "").trim();
        if (!key) return acc;
        if (!acc[key]) {
          acc[key] = {
            hour_start: row.hour_start,
            hour_end: row.hour_end,
            planned_units: [],
            actual_units: [],
            defects_total: [],
            downtime_occurrence: [],
            downtime_minutes: [],
          };
        }
        acc[key].hour_end = row.hour_end ?? acc[key].hour_end;
        acc[key].planned_units.push(getNumber(row.planned_units));
        acc[key].actual_units.push(getNumber(row.actual_units));
        acc[key].defects_total.push(getNumber(row.defects_total));
        acc[key].downtime_occurrence.push(getNumber(row.downtime_occurrence));
        acc[key].downtime_minutes.push(getNumber(row.downtime_minutes));
        return acc;
      }, {});

    const averageValue = (values) => {
      const valid = values.filter((value) => Number.isFinite(value));
      if (!valid.length) return 0;
      return valid.reduce((sum, value) => sum + value, 0) / valid.length;
    };

    return Object.values(grouped)
      .map((entry) => {
        const plannedUnits = averageValue(entry.planned_units);
        const actualUnits = averageValue(entry.actual_units);
        return {
          hour_start: entry.hour_start,
          hour_end: entry.hour_end,
          planned_units: plannedUnits,
          actual_units: actualUnits,
          gap_units: plannedUnits - actualUnits,
          defects_total: averageValue(entry.defects_total),
          downtime_occurrence: averageValue(entry.downtime_occurrence),
          downtime_minutes: averageValue(entry.downtime_minutes),
        };
      })
      .sort((a, b) => String(a.hour_start ?? "").localeCompare(String(b.hour_start ?? "")));
  }, [filteredHourlyRows, getNumber, isValueStream, reportMachine]);

  const machineDailyRows = useMemo(() => {
    if (!isValueStream || reportMachine === "all") return [];
    if (!filteredHourlyRows.length) return [];

    const baseRowsByDate = activeReportRows.reduce((acc, row) => {
      const key = String(row.date ?? "").trim();
      if (key) acc[key] = row;
      return acc;
    }, {});

    const grouped = filteredHourlyRows
      .filter((row) => row.machine === reportMachine)
      .reduce((acc, row) => {
        const key = String(row.date ?? "").trim();
        if (!key) return acc;
        if (!acc[key]) {
          acc[key] = {
            planned_units: 0,
            actual_units: 0,
            planned_count: 0,
            actual_count: 0,
            defects_total: 0,
            downtime_minutes: 0,
          };
        }
        const planned = getNumber(row.planned_units);
        const actual = getNumber(row.actual_units);
        const defects = getNumber(row.defects_total);
        const downtime = getNumber(row.downtime_minutes);
        if (!Number.isNaN(planned)) {
          acc[key].planned_units += planned;
          acc[key].planned_count += 1;
        }
        if (!Number.isNaN(actual)) {
          acc[key].actual_units += actual;
          acc[key].actual_count += 1;
        }
        if (!Number.isNaN(defects)) acc[key].defects_total += defects;
        if (!Number.isNaN(downtime)) acc[key].downtime_minutes += downtime;
        return acc;
      }, {});

    return Object.entries(grouped).map(([date, totals]) => {
      const base = baseRowsByDate[date] || { date };
      const plannedUnits = totals.planned_units || 0;
      const actualUnits = totals.actual_units || 0;
      const defectsTotal = totals.defects_total || 0;
      const fpyActual = actualUnits ? ((actualUnits - defectsTotal) / actualUnits) * 100 : 0;
      const deliveryActual = plannedUnits ? (actualUnits / plannedUnits) * 100 : 0;
      const pphActual = totals.actual_count ? Math.round(actualUnits / totals.actual_count) : 0;
      const pphGoal = totals.planned_count ? Math.round(plannedUnits / totals.planned_count) : 0;

      return {
        ...base,
        date,
        planned_units: plannedUnits,
        actual_units: actualUnits,
        defects_total: defectsTotal,
        downtime_minutes: totals.downtime_minutes || 0,
        quality_fpy_actual_pct: fpyActual,
        delivery_hr_by_hr_actual_pct: deliveryActual,
        cost_productivity_actual_pct: deliveryActual,
        pph_actual: pphActual,
        pph_goal: pphGoal,
      };
    });
  }, [activeReportRows, filteredHourlyRows, getNumber, isValueStream, reportMachine]);

  const buildParetoData = useCallback((metricKey, aggregation = "sum") => {
    if (aggregation === "avg") {
      const totals = paretoSourceRows.reduce((acc, row) => {
        const machine = row.machine || "Unknown";
        const metricValue = getNumber(row[metricKey]);
        if (Number.isNaN(metricValue)) return acc;
        if (!acc[machine]) {
          acc[machine] = { total: 0, count: 0 };
        }
        acc[machine].total += metricValue;
        acc[machine].count += 1;
        return acc;
      }, {});

      return Object.entries(totals)
        .map(([machine, stats]) => {
          const avg = stats.count ? stats.total / stats.count : 0;
          return { machine, value: Math.round(avg * 100) / 100 };
        })
        .sort((a, b) => b.value - a.value);
    }

    if (aggregation === "avgByDay") {
      const byMachineByDate = paretoSourceRows.reduce((acc, row) => {
        const machine = row.machine || "Unknown";
        const date = String(row.date || "").trim();
        const metricValue = getNumber(row[metricKey]);
        if (!date || Number.isNaN(metricValue)) return acc;

        if (!acc[machine]) acc[machine] = {};
        acc[machine][date] = (acc[machine][date] || 0) + metricValue;
        return acc;
      }, {});

      return Object.entries(byMachineByDate)
        .map(([machine, dateTotals]) => {
          const dailyValues = Object.values(dateTotals);
          const average = dailyValues.length
            ? dailyValues.reduce((sum, value) => sum + value, 0) / dailyValues.length
            : 0;
          return { machine, value: Math.round(average * 100) / 100 };
        })
        .sort((a, b) => b.value - a.value);
    }

    const totals = paretoSourceRows.reduce((acc, row) => {
      const machine = row.machine || "Unknown";
      const metricValue = getNumber(row[metricKey]);
      if (!Number.isNaN(metricValue)) {
        acc[machine] = (acc[machine] || 0) + metricValue;
      }
      return acc;
    }, {});
    return Object.entries(totals)
      .map(([machine, value]) => ({ machine, value }))
      .sort((a, b) => b.value - a.value);
  }, [getNumber, paretoSourceRows]);

  const qualityPareto = useMemo(() => buildParetoData("defects_total", "avgByDay"), [buildParetoData]);
  const downtimePareto = useMemo(() => buildParetoData("downtime_minutes", "avgByDay"), [buildParetoData]);
  const pphPareto = useMemo(() => buildParetoData("actual_units", "avg").sort((a, b) => a.value - b.value), [buildParetoData]);
  const qualityParetoMax = useMemo(
    () => (qualityPareto.length ? Math.max(...qualityPareto.map((entry) => entry.value)) : 0),
    [qualityPareto],
  );
  const downtimeParetoMax = useMemo(
    () => (downtimePareto.length ? Math.max(...downtimePareto.map((entry) => entry.value)) : 0),
    [downtimePareto],
  );
  const pphParetoMin = useMemo(
    () => (pphPareto.length ? Math.min(...pphPareto.map((entry) => entry.value)) : 0),
    [pphPareto],
  );
  const parseHourValue = useCallback((value) => {
    if (Number.isFinite(value)) return value;
    const text = String(value ?? "").trim().toLowerCase();
    if (!text) return NaN;

    const match = text.match(/(\d{1,2})(?::(\d{1,2}))?\s*(am|pm)?/i);
    if (!match) return NaN;

    let hour = Number(match[1]);
    const minutes = Number(match[2] || 0);
    const meridiem = match[3];

    if (!Number.isFinite(hour) || !Number.isFinite(minutes)) return NaN;
    if (meridiem === "pm" && hour < 12) hour += 12;
    if (meridiem === "am" && hour === 12) hour = 0;

    return hour + minutes / 60;
  }, []);

  const machineTimeWindowTotals = useMemo(() => {
    if (!isValueStream || reportMachine === "all") return [];

    const windows = [
      { key: "08-10", label: "08:00-10:00", startHour: 8, endHour: 10 },
      { key: "10-12", label: "10:00-12:00", startHour: 10, endHour: 12 },
      { key: "12-14", label: "12:00-14:00", startHour: 12, endHour: 14 },
      { key: "14-16", label: "14:00-16:00", startHour: 14, endHour: 16 },
    ];

    const totalsByWindowByDate = windows.reduce((acc, window) => {
      acc[window.key] = {};
      return acc;
    }, {});

    filteredHourlyRows
      .filter((row) => row.machine === reportMachine)
      .forEach((row) => {
        const hour = parseHourValue(row.hour_start);
        if (!Number.isFinite(hour)) return;

        const matchingWindow = windows.find(
          (window) => hour >= window.startHour && hour < window.endHour,
        );
        if (!matchingWindow) return;

        const date = String(row.date || "").trim();
        if (!date) return;

        if (!totalsByWindowByDate[matchingWindow.key][date]) {
          totalsByWindowByDate[matchingWindow.key][date] = {
            defects_total: 0,
            downtime_minutes: 0,
          };
        }

        const defects = getNumber(row.defects_total);
        const downtime = getNumber(row.downtime_minutes);

        if (!Number.isNaN(defects)) {
          totalsByWindowByDate[matchingWindow.key][date].defects_total += defects;
        }
        if (!Number.isNaN(downtime)) {
          totalsByWindowByDate[matchingWindow.key][date].downtime_minutes += downtime;
        }
      });

    return windows.map((window) => {
      const byDate = Object.values(totalsByWindowByDate[window.key] || {});
      const averageDefects = byDate.length
        ? byDate.reduce((sum, day) => sum + (day.defects_total || 0), 0) / byDate.length
        : 0;
      const averageDowntime = byDate.length
        ? byDate.reduce((sum, day) => sum + (day.downtime_minutes || 0), 0) / byDate.length
        : 0;

      return {
        period: window.label,
        defects_total: Math.round(averageDefects * 100) / 100,
        downtime_minutes: Math.round(averageDowntime * 100) / 100,
      };
    });
  }, [filteredHourlyRows, getNumber, isValueStream, parseHourValue, reportMachine]);
  const machineTimeWindowDefectsMax = useMemo(
    () =>
      machineTimeWindowTotals.length
        ? Math.max(...machineTimeWindowTotals.map((entry) => entry.defects_total))
        : 0,
    [machineTimeWindowTotals],
  );
  const machineTimeWindowDowntimeMax = useMemo(
    () =>
      machineTimeWindowTotals.length
        ? Math.max(...machineTimeWindowTotals.map((entry) => entry.downtime_minutes))
        : 0,
    [machineTimeWindowTotals],
  );

  const defectTypePareto = useMemo(() => {
    if (!paretoSourceRows.length) return [];
    const defectKeys = ["d1", "d2", "d3", "d4", "d5"];
    const totalsByDate = {};

    paretoSourceRows.forEach((row) => {
      const date = String(row.date || "").trim();
      if (!date) return;
      if (!totalsByDate[date]) {
        totalsByDate[date] = defectKeys.reduce((acc, key) => {
          acc[key] = 0;
          return acc;
        }, {});
      }

      defectKeys.forEach((key) => {
        const value = getNumber(row[key]);
        if (!Number.isNaN(value)) {
          totalsByDate[date][key] += value;
        }
      });
    });

    const dateEntries = Object.values(totalsByDate);
    if (!dateEntries.length) return [];

    return defectKeys
      .map((key) => {
        const avg = dateEntries.reduce((sum, day) => sum + (day[key] || 0), 0) / dateEntries.length;
        return { defect: key.toUpperCase(), value: Math.round(avg * 100) / 100 };
      })
      .sort((a, b) => b.value - a.value);
  }, [getNumber, paretoSourceRows]);
  const defectTypeParetoMax = useMemo(
    () => (defectTypePareto.length ? Math.max(...defectTypePareto.map((entry) => entry.value)) : 0),
    [defectTypePareto],
  );

  const reportSourceRows = machineDailyRows.length ? machineDailyRows : activeReportRows;
  const filteredReportRows = reportSourceRows
    .filter((row) => {
      if (!reportStartDate && !reportEndDate) return true;
      const rowDate = parseDateValue(row.date);
      if (Number.isNaN(rowDate)) return true;
      const startDate = reportStartDate ? parseDateValue(reportStartDate) : NaN;
      const endDate = reportEndDate ? parseDateValue(reportEndDate) : NaN;
      if (!Number.isNaN(startDate) && rowDate < startDate) return false;
      if (!Number.isNaN(endDate) && rowDate > endDate) return false;
      return true;
    })
    .filter((row) => {
      if (!plantKey) return true;
      const rowPlant = getRowPlantNormalized(row);
      if (!rowPlant) return true;
      return rowPlant === normalizePlant(reportPlant);
    })
    .filter((row) => {
      if (isValueStream) return true;
      if (reportValueStream === "all") return true;
      return getRowValueStream(row) === reportValueStream;
    })
    .filter((row) => {
      if (machineDailyRows.length) return true;
      if (isValueStream && reportValueStream === "QCS") return true;
      if (reportMachine === "all") return true;
      return getRowMachine(row) === reportMachine;
    })
    .sort((a, b) => {
      if (!reportSort) return 0;
      const [key, direction] = reportSort.split(":");
      const multiplier = direction === "desc" ? -1 : 1;
      if (key === "date") {
        const aDate = parseDateValue(a.date);
        const bDate = parseDateValue(b.date);
        if (!Number.isNaN(aDate) && !Number.isNaN(bDate)) {
          return (aDate - bDate) * multiplier;
        }
        return String(a.date ?? "").localeCompare(String(b.date ?? "")) * multiplier;
      }
      const aValue = getNumber(a[key]);
      const bValue = getNumber(b[key]);
      if (!Number.isNaN(aValue) && !Number.isNaN(bValue)) {
        return (aValue - bValue) * multiplier;
      }
      return String(a[key] ?? "").localeCompare(String(b[key] ?? "")) * multiplier;
    });

  const reportSummary = filteredReportRows.reduce(
    (acc, row) => {
      const safetyGoal = getNumber(
        isValueStream ? row.safety_incidents_target : row.safety_incidents_goal,
      );
      const safetyActual = getNumber(
        isValueStream ? row.safety_incidents_actual : row.safety_incidents_actual,
      );
      const defectsGoal = getNumber(
        isValueStream ? row.quality_fpy_target_pct : row.quality_customer_defects_goal,
      );
      const defectsActual = getNumber(
        isValueStream ? row.quality_fpy_actual_pct : row.quality_customer_defects_actual,
      );
      const otifGoal = getNumber(
        isValueStream ? row.delivery_hr_by_hr_target_pct : row.delivery_otif_goal_pct,
      );
      const otifActual = getNumber(
        isValueStream ? row.delivery_hr_by_hr_actual_pct : row.delivery_otif_actual_pct,
      );
      const productivityGoal = getNumber(
        isMachineLevelValueStream
          ? row.pph_goal
          : isValueStream
            ? row.cost_productivity_target_pct
            : row.cost_productivity_goal_pct,
      );
      const productivityActual = getNumber(
        isMachineLevelValueStream ? row.pph_actual : row.cost_productivity_actual_pct,
      );
      const overtimeGoal = isValueStream ? 50 : getNumber(row.cost_overtime_goal_pct);
      const overtimeActual = getNumber(
        isValueStream ? row.downtime_minutes : row.cost_overtime_actual_pct,
      );
      const crossTrainingGoal = getNumber(
        isValueStream ? row.people_cross_training_target_pct : row.people_cross_training_goal_pct,
      );
      const crossTrainingActual = getNumber(
        isValueStream ? row.people_cross_training_actual_pct : row.people_cross_training_actual_pct,
      );
      const absenteeismGoal = isValueStream ? 0 : getNumber(row.people_absenteeism_goal_pct);
      const absenteeismActual = getNumber(
        isValueStream ? row.hours_missed_goal : row.people_absenteeism_actual_pct,
      );

      acc.count += 1;
      if (!Number.isNaN(safetyActual)) acc.safetyActualTotal += safetyActual;
      if (!Number.isNaN(safetyGoal)) acc.safetyGoalTotal += safetyGoal;
      if (!Number.isNaN(defectsActual)) acc.defectsActualTotal += defectsActual;
      if (!Number.isNaN(defectsGoal)) acc.defectsGoalTotal += defectsGoal;
      if (!Number.isNaN(otifActual)) acc.otifActualTotal += otifActual;
      if (!Number.isNaN(otifGoal)) acc.otifGoalTotal += otifGoal;
      if (!Number.isNaN(productivityActual)) acc.productivityActualTotal += productivityActual;
      if (!Number.isNaN(productivityGoal)) acc.productivityGoalTotal += productivityGoal;
      if (!Number.isNaN(overtimeActual)) acc.overtimeActualTotal += overtimeActual;
      if (!Number.isNaN(overtimeGoal)) acc.overtimeGoalTotal += overtimeGoal;
      if (!Number.isNaN(crossTrainingActual)) acc.crossTrainingActualTotal += crossTrainingActual;
      if (!Number.isNaN(crossTrainingGoal)) acc.crossTrainingGoalTotal += crossTrainingGoal;
      if (!Number.isNaN(absenteeismActual)) acc.absenteeismActualTotal += absenteeismActual;
      if (!Number.isNaN(absenteeismGoal)) acc.absenteeismGoalTotal += absenteeismGoal;

      if (!Number.isNaN(defectsGoal) && !Number.isNaN(defectsActual)) {
        acc.defectsMet += isValueStream
          ? defectsActual >= defectsGoal
            ? 1
            : 0
          : defectsActual <= defectsGoal
            ? 1
            : 0;
      }
      if (!Number.isNaN(otifGoal) && !Number.isNaN(otifActual)) {
        acc.otifMet += otifActual >= otifGoal ? 1 : 0;
      }
      if (!Number.isNaN(productivityGoal) && !Number.isNaN(productivityActual)) {
        acc.productivityMet += productivityActual >= productivityGoal ? 1 : 0;
      }
      if (!Number.isNaN(overtimeActual)) {
        acc.overtimeMet += overtimeActual <= overtimeGoal ? 1 : 0;
      }
      if (!Number.isNaN(crossTrainingGoal) && !Number.isNaN(crossTrainingActual)) {
        acc.crossTrainingMet += crossTrainingActual >= crossTrainingGoal ? 1 : 0;
      }
      if (!Number.isNaN(absenteeismActual)) {
        acc.absenteeismMet += absenteeismActual <= absenteeismGoal ? 1 : 0;
      }
      return acc;
    },
    {
      count: 0,
      safetyActualTotal: 0,
      safetyGoalTotal: 0,
      defectsActualTotal: 0,
      otifActualTotal: 0,
      productivityActualTotal: 0,
      productivityGoalTotal: 0,
      overtimeActualTotal: 0,
      overtimeGoalTotal: 0,
      crossTrainingActualTotal: 0,
      crossTrainingGoalTotal: 0,
      absenteeismActualTotal: 0,
      absenteeismGoalTotal: 0,
      defectsGoalTotal: 0,
      otifGoalTotal: 0,
      defectsMet: 0,
      otifMet: 0,
      productivityMet: 0,
      overtimeMet: 0,
      crossTrainingMet: 0,
      absenteeismMet: 0,
    },
  );

  const average = (total) => (reportSummary.count ? total / reportSummary.count : 0);

  const deviationPercent = (actualAvg, goalAvg) => {
    if (!goalAvg) return 0;
    return Math.abs((actualAvg - goalAvg) / goalAvg) * 100;
  };

  const getDeviationStatus = (actualAvg, goalAvg, threshold, isMet) => {
    if (isMet) return "green";
    if (!goalAvg) return "neutral";
    const deviation = deviationPercent(actualAvg, goalAvg);
    if (deviation > threshold) return "red";
    if (deviation > 0) return "yellow";
    return "green";
  };

  const isLowerBetterMet = (actualAvg, goalAvg) => {
    if (!Number.isFinite(actualAvg) || !Number.isFinite(goalAvg)) return false;
    return actualAvg <= goalAvg;
  };
  const isHigherBetterMet = (actualAvg, goalAvg) => {
    if (!Number.isFinite(actualAvg) || !Number.isFinite(goalAvg)) return false;
    return actualAvg >= goalAvg;
  };

  const chartData = useMemo(
    () => {
      if (trendGranularity === "day") {
        return filteredReportRows.map((row) => ({
          date: row.date,
          ...buildChartMetrics(row),
        }));
      }

      const buckets = new Map();
      filteredReportRows.forEach((row) => {
        const dateKey = formatTrendDateKey(row.date, trendGranularity);
        if (!dateKey) return;
        const metrics = buildChartMetrics(row);
        let bucket = buckets.get(dateKey);
        if (!bucket) {
          bucket = { date: dateKey, sums: {}, counts: {} };
          buckets.set(dateKey, bucket);
        }
        Object.entries(metrics).forEach(([key, value]) => {
          if (!Number.isFinite(value)) return;
          bucket.sums[key] = (bucket.sums[key] || 0) + value;
          bucket.counts[key] = (bucket.counts[key] || 0) + 1;
        });
      });

      return Array.from(buckets.values())
        .map((bucket) => {
          const entry = { date: bucket.date };
          Object.keys(bucket.sums).forEach((key) => {
            const count = bucket.counts[key] || 0;
            entry[key] = count ? bucket.sums[key] / count : 0;
          });
          return entry;
        })
        .sort((a, b) => String(a.date).localeCompare(String(b.date)));
    },
    [buildChartMetrics, filteredReportRows, formatTrendDateKey, trendGranularity],
  );

  const trendAxisTicks = useMemo(() => {
    if (!chartData.length) return [];
    const dates = chartData.map((entry) => entry.date).filter(Boolean);
    if (!dates.length) return [];
    if (dates.length <= 3) return Array.from(new Set(dates));

    const first = dates[0];
    const middle = dates[Math.floor((dates.length - 1) / 2)];
    const last = dates[dates.length - 1];
    return Array.from(new Set([first, middle, last]));
  }, [chartData]);

  const chartConfigs = useMemo(() => {
    if (isValueStream) {
      return [
        { title: "Safety (incidents)", actualKey: "safetyActual", goalKey: "safetyGoal", unit: "" },
        { title: "Quality FPY (%)", actualKey: "qualityActual", goalKey: "qualityGoal", unit: "%" },
        { title: "Delivery HR-by-HR (%)", actualKey: "otifActual", goalKey: "otifGoal", unit: "%" },
        {
          title: isMachineLevelValueStream ? "PPH" : "Productivity (%)",
          actualKey: "productionActual",
          goalKey: "productionGoal",
          unit: isMachineLevelValueStream ? "" : "%",
        },
        { title: "Downtime (min)", actualKey: "overtimeActual", goalKey: "overtimeGoal", unit: "" },
        { title: "Cross training (%)", actualKey: "crossTrainingActual", goalKey: "crossTrainingGoal", unit: "%" },
      ];
    }
    return [
      { title: "Safety (incidents)", actualKey: "safetyActual", goalKey: "safetyGoal", unit: "" },
      { title: "Quality (customer defects)", actualKey: "qualityActual", goalKey: "qualityGoal", unit: "" },
      { title: "OTIF (%)", actualKey: "otifActual", goalKey: "otifGoal", unit: "%" },
      { title: "Production productivity (%)", actualKey: "productionActual", goalKey: "productionGoal", unit: "%" },
      { title: "Overtime (%)", actualKey: "overtimeActual", goalKey: "overtimeGoal", unit: "%" },
      { title: "Cross training (%)", actualKey: "crossTrainingActual", goalKey: "crossTrainingGoal", unit: "%" },
      { title: "Absenteeism (%)", actualKey: "absenteeismActual", goalKey: "absenteeismGoal", unit: "%" },
    ];
  }, [isMachineLevelValueStream, isValueStream]);

  const chartConfigByKey = useMemo(
    () =>
      chartConfigs.reduce((acc, config) => {
        acc[config.actualKey] = config;
        return acc;
      }, {}),
    [chartConfigs],
  );

  const renderTrendCard = (config, hidden = false) => {
    if (!config) {
      return <div className="report-chart-card report-chart-card--hidden" aria-hidden="true" />;
    }

    if (hidden) {
      return (
        <div className="report-chart-card report-chart-card--hidden" key={config.actualKey} aria-hidden="true" />
      );
    }

    return (
      <div className="report-chart-card" key={config.actualKey}>
        <div className="report-chart-title">{config.title}</div>
        <div className="report-chart-body">
          <ResponsiveContainer width="100%" height={240}>
            <LineChart data={chartData} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
              <CartesianGrid stroke="rgba(148,163,184,0.2)" strokeDasharray="4 4" />
              <XAxis
                dataKey="date"
                ticks={trendAxisTicks}
                interval={0}
                tick={{ fontSize: 10, fill: "#94a3b8" }}
              />
              <YAxis
                tick={{ fontSize: 10, fill: "#94a3b8" }}
                tickFormatter={(value) => `${value}${config.unit}`}
                width={40}
              />
              <Tooltip
                formatter={(value) => {
                  const numeric = Number(value);
                  if (Number.isFinite(numeric)) {
                    return [`${numeric.toFixed(1)}${config.unit}`, ""];
                  }
                  return [`${value}${config.unit}`, ""];
                }}
                labelStyle={{ color: "#0f172a" }}
              />
              <Legend />
              <Line type="monotone" dataKey={config.actualKey} name="Actual" stroke="#22d3ee" strokeWidth={2} dot={false} />
              <Line
                type="monotone"
                dataKey={config.goalKey}
                name="Goal"
                stroke="#94a3b8"
                strokeDasharray="6 4"
                strokeWidth={2}
                dot={false}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>
    );
  };

  const [chartVisibility, setChartVisibility] = useState(() =>
    chartConfigs.reduce((acc, config) => {
      acc[config.actualKey] = true;
      return acc;
    }, {}),
  );

  useEffect(() => {
    setChartVisibility((prev) =>
      chartConfigs.reduce((acc, config) => {
        acc[config.actualKey] = prev[config.actualKey] ?? true;
        return acc;
      }, {}),
    );
  }, [chartConfigs]);

  const handleExport = () => {
    if (!activeReportHeaders.length) return;
    const csvLines = [activeReportHeaders.join(",")];
    filteredReportRows.forEach((row) => {
      const line = activeReportHeaders
        .map((header) => {
          const raw = String(row[header] ?? "");
          return `"${raw.replace(/"/g, '""')}"`;
        })
        .join(",");
      csvLines.push(line);
    });
    const blob = new Blob([csvLines.join("\n")], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = "plant_daily_metrics_filtered.csv";
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
  };

  // Load a past thread from the history sidebar
  const handleLoadThread = useCallback((threadId, messages) => {
    setAgentThreadId(threadId);
    setAgentMessages(messages || []);
    setAgentError("");
    localStorage.setItem('agentThreadId', threadId);
    localStorage.setItem('agentMessages', JSON.stringify(messages || []));
  }, []);

  // Agent chat handler
  const handleAgentSubmit = async (customPrompt = null, metricContext = null, forceNewThread = false) => {
    const prompt = customPrompt || agentInput;
    if (!prompt.trim()) return;

    // Generate thread_id — always fresh when forceNewThread is set (deepdive buttons)
    let threadId = agentThreadId;
    if (!threadId || forceNewThread) {
      threadId = `thread_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      setAgentThreadId(threadId);
      console.log("[handleAgentSubmit] Created NEW thread:", threadId);
    } else {
      console.log("[handleAgentSubmit] Reusing EXISTING thread:", threadId);
    }
    console.log("[handleAgentSubmit] Sending prompt:", prompt);

    // Add user message — fresh array on forceNewThread, append on typed continuation
    const userMessage = { role: "user", content: prompt };
    if (forceNewThread) {
      setAgentMessages([userMessage]);
    } else {
      setAgentMessages((prev) => [...prev, userMessage]);
    }
    setAgentInput("");
    setAgentLoading(true);
    setAgentError("");

    try {
      const defaultAgentApiUrl =
        window.location.hostname === "localhost"
          ? "/api/agent"
          : "https://stratalign-factory-api.azurewebsites.net/api/agent";
      const agentApiUrl =
        agentConfig?.agent?.apiUrl || process.env.REACT_APP_AGENT_API_URL || defaultAgentApiUrl;
      
      const requestBody = {
        prompt: prompt,
        thread_id: threadId,
        ...(metricContext && {
          plant: metricContext.plant || reportPlant,
          valueStream: metricContext.valueStream || reportValueStream,
          machine: metricContext.machine || reportMachine,
          startDate: metricContext.startDate || reportStartDate,
          endDate: metricContext.endDate || reportEndDate,
          metric: metricContext.metric
        }),
        // If no metric context, include filter context
        ...(!metricContext && {
          plant: reportPlant,
          valueStream: reportValueStream,
          machine: reportMachine,
          startDate: reportStartDate,
          endDate: reportEndDate
        })
      };

      // Get access token for authentication
      const accessToken = await getAccessToken();
      if (!accessToken && !bypassLogin) {
        throw new Error("User authentication required. Please sign in.");
      }

      // Build request headers with optional Bearer token
      const headers = { "Content-Type": "application/json" };
      if (accessToken) {
        headers["Authorization"] = `Bearer ${accessToken}`;
      }

      const response = await fetch(agentApiUrl, {
        method: "POST",
        headers,
        body: JSON.stringify(requestBody)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || "Agent API error");
      }

      const data = await response.json();
      console.log("[handleAgentSubmit] Backend response:", {
        thread_id: data.thread_id,
        messageLength: data.message?.length,
        messagePreview: data.message?.substring(0, 100)
      });
      
      // Append assistant message directly — no fake streaming
      setAgentMessages(prev => [...prev, {
        role: "assistant",
        content: data.message,
        charts: data.charts || [],
        agent: data.activeAgent || null
      }]);
      setAgentLoading(false);
    } catch (error) {
      setAgentError(error.message || "Failed to get response from agent");
      const errorMessage = {
        role: "assistant",
        content: `Error: ${error.message}. Please try again.`
      };
      setAgentMessages((prev) => [...prev, errorMessage]);
      setAgentLoading(false);
    }
  };

  // ── A3 PDF Export ────────────────────────────────────────────────────────────
  // isA3Message is imported from a3DetectionUtils for testability (see top-of-file import)

  const handleDownloadA3Pdf = async (content, idx) => {
    setGeneratingA3Idx(idx);
    try {
      const { exportA3ToPdf } = await import('./AgenticAI/a3PdfExport');
      await exportA3ToPdf(content, idx);
    } catch (err) {
      console.error('[A3 PDF Export] Failed — message:', err?.message);
      console.error('[A3 PDF Export] Stack:', err?.stack);
      console.error('[A3 PDF Export] Full error object:', err);
      // ChunkLoadError means the browser cached an old JS bundle after a deployment.
      // Refreshing the page loads the new chunks and fixes it.
      const isChunkError = err?.name === 'ChunkLoadError' || /loading chunk/i.test(err?.message || '');
      if (isChunkError) {
        alert('PDF generation failed because your browser has a cached version of the app.\n\nPlease refresh the page (Ctrl+R or Cmd+R) and try again.');
      } else {
        alert('PDF generation failed. Please try again.\n\nDetails: ' + (err?.message || String(err)));
      }
    } finally {
      setGeneratingA3Idx(null);
    }
  };

  // Helper to build prompt from filter context (mirrors backend logic)
  const buildPromptFromContext = (metric, plant, valueStream, machine, startDate, endDate) => {
    const parts = ["In my factory"];

    if (plant && plant !== "all") {
      parts.push(plant);
    }

    if (valueStream && valueStream !== "all") {
      parts.push(", in value stream " + valueStream);
    }

    if (machine && machine !== "all") {
      parts.push(", in machine " + machine);
    }

    // Use dates with proper formatting
    const displayStartDate = startDate || "2025-06-01";
    const displayEndDate = endDate || "2025-06-30";

    if (displayStartDate || displayEndDate) {
      parts.push(", between");
      if (displayStartDate) {
        parts.push(formatDateString(displayStartDate));
      }
      if (displayStartDate && displayEndDate) {
        parts.push("and");
      }
      if (displayEndDate) {
        parts.push(formatDateString(displayEndDate));
      }
    }

    parts.push(", help me solve problem related to " + metric);

    return parts.join(" ");
  };

  // Helper to format date string - handle ISO dates without timezone shift
  const formatDateString = (dateStr) => {
    if (!dateStr) return "";
    // If already in ISO format (YYYY-MM-DD), return as-is
    if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
      return dateStr;
    }
    // Otherwise parse and format
    const date = new Date(dateStr + "T00:00:00");
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  // Helper to trigger metric insights
  const requestMetricInsights = (metric) => {
    const prompt = buildPromptFromContext(
      metric,
      reportPlant,
      reportValueStream,
      reportMachine,
      reportStartDate,
      reportEndDate
    );
    
    // Daily Management deepdive always starts a fresh thread — context comes from
    // the button's built prompt, not from prior chat history.
    // NOTE: response quality issue (agent re-asks for info already in prompt) is tracked
    // separately and will be addressed in a future issue.
    const threadId = `thread_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    setAgentThreadId(threadId);
    console.log("[requestMetricInsights] Created NEW thread:", threadId);
    console.log("[requestMetricInsights] Built prompt:", prompt);
    console.log("[requestMetricInsights] Metric:", metric);
    
    // Fresh message array — no prior context carried over
    const userMessage = { role: "user", content: prompt };
    setAgentMessages([userMessage]);
    setAgentInput("");
    setAgentLoading(true);
    setAgentError("");

    // Auto-submit the prompt
    const submitMetricPrompt = async () => {
      try {
        const defaultAgentApiUrl =
          window.location.hostname === "localhost"
            ? "/api/agent"
            : "https://stratalign-factory-api.azurewebsites.net/api/agent";
        const agentApiUrl =
          agentConfig?.agent?.apiUrl || process.env.REACT_APP_AGENT_API_URL || defaultAgentApiUrl;
        
        console.log("Agent API URL:", agentApiUrl);
        console.log("Sending prompt:", prompt);
        console.log("Thread ID:", threadId);
        
        const requestBody = {
          prompt: prompt,
          thread_id: threadId,
          plant: reportPlant,
          valueStream: reportValueStream,
          machine: reportMachine,
          startDate: reportStartDate,
          endDate: reportEndDate,
          metric: metric
        };

        console.log("Request body:", requestBody);

        // Get access token for authentication
        const accessToken = await getAccessToken();
        if (!accessToken && !bypassLogin) {
          throw new Error("User authentication required. Please sign in.");
        }

        // Build request headers with optional Bearer token
        const headers = { "Content-Type": "application/json" };
        if (accessToken) {
          headers["Authorization"] = `Bearer ${accessToken}`;
        }

        const response = await fetch(agentApiUrl, {
          method: "POST",
          headers,
          body: JSON.stringify(requestBody)
        });

        console.log("Response status:", response.status);

        if (!response.ok) {
          const errorData = await response.text();
          console.error("Agent error response:", errorData);
          throw new Error(`API returned ${response.status}: ${errorData}`);
        }

        const data = await response.json();
        console.log("Agent response:", data);
        
        const assistantMessage = {
          role: "assistant",
          content: data.message,
          charts: data.charts || [],
          agent:  data.activeAgent || null
        };
        setAgentMessages((prev) => [...prev, assistantMessage]);
      } catch (error) {
        console.error("Agent request error:", error);
        setAgentError(error.message || "Failed to get response from agent");
        const errorMessage = {
          role: "assistant",
          content: `Error: ${error.message}. Please try again.`
        };
        setAgentMessages((prev) => [...prev, errorMessage]);
      } finally {
        setAgentLoading(false);
      }
    };

    submitMetricPrompt();
  };

  useEffect(() => {
    if (account && forceLogin && typeof window !== "undefined") {
      window.history.replaceState(null, "", "/");
    }
  }, [account, forceLogin]);

  useEffect(() => {
    if (account) {
      setActivePage("Factory Floor");
    }
  }, [account]);

  if (!account) {
    // Full-screen login page styled like the provided image
    return <LoginScreen />;
  }

  const safetyStatus = getDeviationStatus(
    average(reportSummary.safetyActualTotal),
    average(reportSummary.safetyGoalTotal),
    1,
    isLowerBetterMet(
      average(reportSummary.safetyActualTotal),
      average(reportSummary.safetyGoalTotal),
    ),
  );
  const qualityStatus = getDeviationStatus(
    average(reportSummary.defectsActualTotal),
    average(reportSummary.defectsGoalTotal),
    10,
    isValueStream
      ? isHigherBetterMet(
          average(reportSummary.defectsActualTotal),
          average(reportSummary.defectsGoalTotal),
        )
      : isLowerBetterMet(
          average(reportSummary.defectsActualTotal),
          average(reportSummary.defectsGoalTotal),
        ),
  );
  const deliveryStatus = getDeviationStatus(
    average(reportSummary.otifActualTotal),
    average(reportSummary.otifGoalTotal),
    2,
    isHigherBetterMet(
      average(reportSummary.otifActualTotal),
      average(reportSummary.otifGoalTotal),
    ),
  );
  const productivityStatus = getDeviationStatus(
    average(reportSummary.productivityActualTotal),
    average(reportSummary.productivityGoalTotal),
    2,
    isHigherBetterMet(
      average(reportSummary.productivityActualTotal),
      average(reportSummary.productivityGoalTotal),
    ),
  );
  const overtimeStatus = getDeviationStatus(
    average(reportSummary.overtimeActualTotal),
    average(reportSummary.overtimeGoalTotal),
    10,
    isLowerBetterMet(
      average(reportSummary.overtimeActualTotal),
      average(reportSummary.overtimeGoalTotal),
    ),
  );
  const crossTrainingStatus = getDeviationStatus(
    average(reportSummary.crossTrainingActualTotal),
    average(reportSummary.crossTrainingGoalTotal),
    10,
    isHigherBetterMet(
      average(reportSummary.crossTrainingActualTotal),
      average(reportSummary.crossTrainingGoalTotal),
    ),
  );
  const absenteeismStatus = getDeviationStatus(
    average(reportSummary.absenteeismActualTotal),
    average(reportSummary.absenteeismGoalTotal),
    10,
    isLowerBetterMet(
      average(reportSummary.absenteeismActualTotal),
      average(reportSummary.absenteeismGoalTotal),
    ),
  );

  return (
    <div className="app-shell">
      <header className="app-header">
        <div className="app-title">StratAlign LLC {isPPE && <span style={{fontSize: "0.8em", marginLeft: "0.5em", opacity: 0.7}}>(PPE)</span>}</div>
        <nav className="tabs">
          {showFactoryFloor && (
            <button
              className={`tab ${activePage === "Factory Floor" ? "tab-active" : ""}`}
              onClick={() => setActivePage("Factory Floor")}
            >Factory Floor</button>
          )}
          {showAgentChat && (
            <button
              className={`tab ${activePage === "Agentic AI" ? "tab-active" : ""}`}
              onClick={() => setActivePage("Agentic AI")}
            >Agentic AI</button>
          )}
          {showDailyManagement && (
            <button
              className={`tab ${activePage === "Reporting" ? "tab-active" : ""}`}
              onClick={() => setActivePage("Reporting")}
            >Daily Management</button>
          )}
          {showDailyManagement && (
            <button
              className={`tab ${activePage === "A3 & Kaizen" ? "tab-active" : ""}`}
              onClick={() => setActivePage("A3 & Kaizen")}
            >A3 & Kaizen</button>
          )}
        </nav>
        <div className="app-actions">
          <span style={{ color: "var(--text-muted)", fontSize: "0.7em" }}>Signed in as {account.username}</span>
          <button className="btn" onClick={logout}>Sign Out</button>
        </div>
      </header>
      <main className="app-main">
        {activePage === "factory_floor_old" && (
          <FactoryFloorView
            onAgentDeepDive={(prompt) => {
              setActivePage("Agentic AI");
              handleAgentSubmit(prompt, null, true);
            }}
          />
        )}
        {activePage === "Reporting" && (
          <div className="card">
            <div className="report-header">
              <div>
                <h2 className="report-title">Daily Management</h2>
              </div>
              <div className="report-meta">
              </div>
            </div>
            <div className="report-controls">
              <label className="report-control">
                <span>Plant</span>
                <select
                  className="report-input"
                  value={reportPlant}
                  onChange={(event) => setReportPlant(event.target.value)}
                >
                  {plantOptions.map((plant) => (
                    <option key={plant} value={plant}>
                      {plant}
                    </option>
                  ))}
                </select>
              </label>
              <label className="report-control">
                <span>Value stream</span>
                <select
                  className="report-input"
                  value={reportValueStream}
                  onChange={(event) => setReportValueStream(event.target.value)}
                >
                  <option value="all">Plant level</option>
                  {valueStreamOptions.map((stream) => (
                    <option key={stream} value={stream}>
                      {stream}
                    </option>
                  ))}
                </select>
              </label>
              <label className="report-control">
                <span>Machine</span>
                <select
                  className="report-input"
                  value={reportMachine}
                  onChange={(event) => setReportMachine(event.target.value)}
                >
                  <option value="all">All machines</option>
                  {machineOptions.map((machine) => (
                    <option key={machine} value={machine}>
                      {machine}
                    </option>
                  ))}
                </select>
              </label>
              <label className="report-control">
                <span>Start date</span>
                <input
                  className="report-input"
                  type="date"
                  value={reportStartDate}
                  onChange={(event) => setReportStartDate(event.target.value)}
                />
              </label>
              <label className="report-control">
                <span>End date</span>
                <input
                  className="report-input"
                  type="date"
                  value={reportEndDate}
                  onChange={(event) => setReportEndDate(event.target.value)}
                />
              </label>
              <label className="report-control">
                <span>Trend view</span>
                <select
                  className="report-input"
                  value={trendGranularity}
                  onChange={(event) => setTrendGranularity(event.target.value)}
                >
                  <option value="day">By day</option>
                  <option value="week">By week</option>
                  <option value="month">By month</option>
                </select>
              </label>
            </div>
            <div className="report-actions">
              <div className="report-toggle-list">
                {chartConfigs.map((config) => (
                  <label className="report-toggle" key={config.actualKey}>
                    <input
                      type="checkbox"
                      checked={chartVisibility[config.actualKey]}
                      onChange={() =>
                        setChartVisibility((prev) => ({
                          ...prev,
                          [config.actualKey]: !prev[config.actualKey],
                        }))
                      }
                    />
                    <span>{config.title}</span>
                  </label>
                ))}
              </div>
              <button className="btn btn-primary" onClick={handleExport}>
                Export CSV
              </button>
            </div>
            {reportStatus === "loading" && (
              <p className="report-status">Loading report data…</p>
            )}
            {reportStatus === "error" && (
              <p className="report-status report-error">{reportError}</p>
            )}
            {reportStatus === "ready" && (
              <>
                <div className="report-kpi-chart-grid report-kpi-chart-grid--split">
                  <div className="report-kpi-grid">
                    <div className="report-kpi-group">
                      <span className="report-kpi-category">
                        <strong>S</strong>afety
                      </span>
                      <div className={`report-kpi report-kpi--${safetyStatus}`}>
                        <span className="report-kpi-label">Safety incidents (sum)</span>
                        <div className="report-kpi-values">
                          <strong className="report-kpi-value">
                            {reportSummary.safetyActualTotal.toFixed(0)}
                          </strong>
                          <strong className="report-kpi-goal">
                            Goal {reportSummary.safetyGoalTotal.toFixed(0)}
                          </strong>
                        </div>
                        <span className="report-kpi-sub report-kpi-sub--ghost">&nbsp;</span>
                        {(safetyStatus === "red" || safetyStatus === "yellow") && (
                          <button
                            className="report-kpi-insights"
                            onClick={() => {
                              setActivePage("Agentic AI");
                              requestMetricInsights("Safety");
                            }}
                          >
                            Agentic AI Deepdive
                          </button>
                        )}
                      </div>
                    </div>
                    <div className="report-kpi-group">
                      <span className="report-kpi-category">
                        <strong>Q</strong>uality
                      </span>
                      <div className={`report-kpi report-kpi--${qualityStatus}`}>
                        <span className="report-kpi-label">{isValueStream ? "FPY avg" : "Defects avg"}</span>
                        <div className="report-kpi-values">
                          <strong className="report-kpi-value">
                            {average(reportSummary.defectsActualTotal).toFixed(2)}
                            {isValueStream ? "%" : ""}
                          </strong>
                          <strong className="report-kpi-goal">
                            Goal {average(reportSummary.defectsGoalTotal).toFixed(2)}
                            {isValueStream ? "%" : ""}
                          </strong>
                        </div>
                        <span className="report-kpi-sub">
                          {reportSummary.defectsMet}/{reportSummary.count} days met goal
                        </span>
                        {(qualityStatus === "red" || qualityStatus === "yellow") && (
                          <button
                            className="report-kpi-insights"
                            onClick={() => {
                              setActivePage("Agentic AI");
                              requestMetricInsights(isValueStream ? "FPY" : "Defect");
                            }}
                          >
                            Agentic AI Deepdive
                          </button>
                        )}
                      </div>
                    </div>
                    <div className="report-kpi-group">
                      <span className="report-kpi-category">
                        <strong>D</strong>elivery
                      </span>
                      <div className={`report-kpi report-kpi--${deliveryStatus}`}>
                        <span className="report-kpi-label">{isValueStream ? "Delivery avg" : "OTIF avg"}</span>
                        <div className="report-kpi-values">
                          <strong className="report-kpi-value">
                            {average(reportSummary.otifActualTotal).toFixed(2)}%
                          </strong>
                          <strong className="report-kpi-goal">
                            Goal {average(reportSummary.otifGoalTotal).toFixed(2)}%
                          </strong>
                        </div>
                        <span className="report-kpi-sub">
                          {reportSummary.otifMet}/{reportSummary.count} days met goal
                        </span>
                        {(deliveryStatus === "red" || deliveryStatus === "yellow") && (
                          <button
                            className="report-kpi-insights"
                            onClick={() => {
                              setActivePage("Agentic AI");
                              requestMetricInsights(isValueStream ? "Delivery" : "OTIF");
                            }}
                          >
                            Agentic AI Deepdive
                          </button>
                        )}
                      </div>
                    </div>
                    <div className="report-kpi-group">
                      <span className="report-kpi-category">
                        <strong>C</strong>ost
                      </span>
                      <div className="report-kpi-stack">
                        <div className={`report-kpi report-kpi--${productivityStatus}`}>
                          <span className="report-kpi-label">
                            {isMachineLevelValueStream ? "PPH avg" : "Productivity avg"}
                          </span>
                          <div className="report-kpi-values">
                            <strong className="report-kpi-value">
                              {isMachineLevelValueStream
                                ? String(Math.round(average(reportSummary.productivityActualTotal)))
                                : `${average(reportSummary.productivityActualTotal).toFixed(2)}%`}
                            </strong>
                            <strong className="report-kpi-goal">
                              {isMachineLevelValueStream
                                ? `Goal ${Math.round(average(reportSummary.productivityGoalTotal))}`
                                : `Goal ${average(reportSummary.productivityGoalTotal).toFixed(2)}%`}
                            </strong>
                          </div>
                          <span className="report-kpi-sub">
                            {reportSummary.productivityMet}/{reportSummary.count} days met goal
                          </span>
                          {(productivityStatus === "red" || productivityStatus === "yellow") && (
                            <button
                              className="report-kpi-insights"
                              onClick={() => {
                                setActivePage("Agentic AI");
                                requestMetricInsights("Productivity");
                              }}
                            >
                              Agentic AI Deepdive
                            </button>
                          )}
                        </div>
                        <div className={`report-kpi report-kpi--${overtimeStatus}`}>
                          <span className="report-kpi-label">{isValueStream ? "Downtime avg (min)" : "Overtime avg"}</span>
                          <div className="report-kpi-values">
                            <strong className="report-kpi-value">
                              {average(reportSummary.overtimeActualTotal).toFixed(2)}
                              {isValueStream ? "" : "%"}
                            </strong>
                            <strong className="report-kpi-goal">
                              Goal {average(reportSummary.overtimeGoalTotal).toFixed(2)}
                              {isValueStream ? "" : "%"}
                            </strong>
                          </div>
                          <span className="report-kpi-sub">
                            {reportSummary.overtimeMet}/{reportSummary.count} days met goal
                          </span>
                          {(overtimeStatus === "red" || overtimeStatus === "yellow") && (
                            <button
                              className="report-kpi-insights"
                              onClick={() => {
                                setActivePage("Agentic AI");
                                requestMetricInsights(isValueStream ? "Downtime" : "Overtime");
                              }}
                            >
                              Agentic AI Deepdive
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="report-kpi-group">
                      <span className="report-kpi-category">
                        <strong>P</strong>eople
                      </span>
                      <div className="report-kpi-stack">
                        <div className={`report-kpi report-kpi--${crossTrainingStatus}`}>
                          <span className="report-kpi-label">Cross-training avg</span>
                          <div className="report-kpi-values">
                            <strong className="report-kpi-value">
                              {average(reportSummary.crossTrainingActualTotal).toFixed(2)}%
                            </strong>
                            <strong className="report-kpi-goal">
                              Goal {average(reportSummary.crossTrainingGoalTotal).toFixed(2)}%
                            </strong>
                          </div>
                          <span className="report-kpi-sub">
                            {reportSummary.crossTrainingMet}/{reportSummary.count} days met goal
                          </span>
                          {(crossTrainingStatus === "red" || crossTrainingStatus === "yellow") && (
                            <button
                              className="report-kpi-insights"
                              onClick={() => {
                                setActivePage("Agentic AI");
                                requestMetricInsights("Cross-training");
                              }}
                            >
                              Agentic AI Deepdive
                            </button>
                          )}
                        </div>
                        {!isValueStream && (
                          <div className={`report-kpi report-kpi--${absenteeismStatus}`}>
                            <span className="report-kpi-label">Absenteeism avg</span>
                            <div className="report-kpi-values">
                              <strong className="report-kpi-value">
                                {average(reportSummary.absenteeismActualTotal).toFixed(2)}%
                              </strong>
                              <strong className="report-kpi-goal">
                                Goal {average(reportSummary.absenteeismGoalTotal).toFixed(2)}%
                              </strong>
                            </div>
                            <span className="report-kpi-sub">
                              {reportSummary.absenteeismMet}/{reportSummary.count} days met goal
                            </span>
                            {(absenteeismStatus === "red" || absenteeismStatus === "yellow") && (
                              <button
                                className="report-kpi-insights"
                                onClick={() => {
                                  setActivePage("Agentic AI");
                                  requestMetricInsights("Absenteeism");
                                }}
                              >
                                Agentic AI Deepdive
                              </button>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="report-trend-grid">
                    <div className="report-trend-row">
                      <div className="report-trend-slot">
                        {renderTrendCard(chartConfigByKey.safetyActual, !chartVisibility.safetyActual)}
                      </div>
                      <div className="report-trend-slot">
                        {renderTrendCard(chartConfigByKey.qualityActual, !chartVisibility.qualityActual)}
                      </div>
                      <div className="report-trend-slot">
                        {renderTrendCard(chartConfigByKey.otifActual, !chartVisibility.otifActual)}
                      </div>
                      <div className="report-trend-stack report-trend-slot">
                        {renderTrendCard(chartConfigByKey.productionActual, !chartVisibility.productionActual)}
                        {renderTrendCard(chartConfigByKey.overtimeActual, !chartVisibility.overtimeActual)}
                      </div>
                      <div className="report-trend-stack report-trend-slot">
                        {!isValueStream &&
                          renderTrendCard(
                            chartConfigByKey.absenteeismActual,
                            !chartVisibility.absenteeismActual,
                          )}
                        {renderTrendCard(
                          chartConfigByKey.crossTrainingActual,
                          !chartVisibility.crossTrainingActual,
                        )}
                      </div>
                    </div>
                  </div>
                </div>
                {isValueStream && (
                  <>
                    <div className="report-pareto-section">
                      <div className="report-pareto-title">
                        Machine Pareto {isValueStream ? `(${reportValueStream})` : ""}
                        {reportMachine !== "all" ? ` - ${reportMachine}` : ""}
                      </div>
                      <div className="report-pareto-grid">
                        {reportMachine === "all" && (
                          <div className="report-chart-card">
                            <div className="report-chart-title">Average defects/day by machine</div>
                            <div className="report-chart-body">
                              {qualityPareto.length > 0 ? (
                                <ResponsiveContainer width="100%" height={240}>
                                  <BarChart data={qualityPareto} margin={{ top: 10, right: 10, left: -10, bottom: 0 }}>
                                    <CartesianGrid stroke="rgba(148,163,184,0.2)" strokeDasharray="4 4" />
                                    <XAxis dataKey="machine" tick={{ fontSize: 10, fill: "#94a3b8" }} interval={0} />
                                    <YAxis tick={{ fontSize: 10, fill: "#94a3b8" }} />
                                    <Tooltip formatter={(value) => [value, "Avg defects/day"]} />
                                    <Bar dataKey="value" name="Avg defects/day">
                                      {qualityPareto.map((entry, index) => (
                                        <Cell
                                          key={`quality-cell-${index}`}
                                          fill={entry.value === qualityParetoMax ? "#ef4444" : "#38bdf8"}
                                        />
                                      ))}
                                    </Bar>
                                  </BarChart>
                                </ResponsiveContainer>
                              ) : (
                                <div className="report-chart-empty">No data</div>
                              )}
                            </div>
                          </div>
                        )}
                        {reportMachine === "all" ? (
                          <div className="report-chart-card">
                            <div className="report-chart-title">Average defect types/day (D1-D5)</div>
                            <div className="report-chart-body">
                              {defectTypePareto.length > 0 ? (
                                <ResponsiveContainer width="100%" height={240}>
                                  <BarChart data={defectTypePareto} margin={{ top: 10, right: 10, left: -10, bottom: 0 }}>
                                    <CartesianGrid stroke="rgba(148,163,184,0.2)" strokeDasharray="4 4" />
                                    <XAxis dataKey="defect" tick={{ fontSize: 10, fill: "#94a3b8" }} interval={0} />
                                    <YAxis tick={{ fontSize: 10, fill: "#94a3b8" }} />
                                    <Tooltip formatter={(value) => [value, "Avg defects/day"]} />
                                    <Bar dataKey="value" name="Avg defects/day">
                                      {defectTypePareto.map((entry, index) => (
                                        <Cell
                                          key={`defect-type-cell-${index}`}
                                          fill={entry.value === defectTypeParetoMax ? "#ef4444" : "#38bdf8"}
                                        />
                                      ))}
                                    </Bar>
                                  </BarChart>
                                </ResponsiveContainer>
                              ) : (
                                <div className="report-chart-empty">No data</div>
                              )}
                            </div>
                          </div>
                        ) : (
                          <>
                            <div className="report-chart-card">
                              <div className="report-chart-title">Average defects/day by time period ({reportMachine})</div>
                              <div className="report-chart-body">
                                {machineTimeWindowTotals.some((entry) => entry.defects_total > 0) ? (
                                  <ResponsiveContainer width="100%" height={240}>
                                    <BarChart data={machineTimeWindowTotals} margin={{ top: 10, right: 10, left: -10, bottom: 0 }}>
                                      <CartesianGrid stroke="rgba(148,163,184,0.2)" strokeDasharray="4 4" />
                                      <XAxis dataKey="period" tick={{ fontSize: 10, fill: "#94a3b8" }} interval={0} />
                                      <YAxis tick={{ fontSize: 10, fill: "#94a3b8" }} />
                                      <Tooltip formatter={(value) => [value, "Avg defects/day"]} />
                                      <Bar dataKey="defects_total" name="Avg defects/day">
                                        {machineTimeWindowTotals.map((entry, index) => (
                                          <Cell
                                            key={`defects-time-cell-${index}`}
                                            fill={entry.defects_total === machineTimeWindowDefectsMax ? "#ef4444" : "#38bdf8"}
                                          />
                                        ))}
                                      </Bar>
                                    </BarChart>
                                  </ResponsiveContainer>
                                ) : (
                                  <div className="report-chart-empty">No data</div>
                                )}
                              </div>
                            </div>
                            <div className="report-chart-card">
                              <div className="report-chart-title">Average downtime minutes/day by time period ({reportMachine})</div>
                              <div className="report-chart-body">
                                {machineTimeWindowTotals.some((entry) => entry.downtime_minutes > 0) ? (
                                  <ResponsiveContainer width="100%" height={240}>
                                    <BarChart data={machineTimeWindowTotals} margin={{ top: 10, right: 10, left: -10, bottom: 0 }}>
                                      <CartesianGrid stroke="rgba(148,163,184,0.2)" strokeDasharray="4 4" />
                                      <XAxis dataKey="period" tick={{ fontSize: 10, fill: "#94a3b8" }} interval={0} />
                                      <YAxis tick={{ fontSize: 10, fill: "#94a3b8" }} />
                                      <Tooltip formatter={(value) => [value, "Avg downtime min/day"]} />
                                      <Bar dataKey="downtime_minutes" name="Avg downtime min/day">
                                        {machineTimeWindowTotals.map((entry, index) => (
                                          <Cell
                                            key={`downtime-time-cell-${index}`}
                                            fill={entry.downtime_minutes === machineTimeWindowDowntimeMax ? "#ef4444" : "#38bdf8"}
                                          />
                                        ))}
                                      </Bar>
                                    </BarChart>
                                  </ResponsiveContainer>
                                ) : (
                                  <div className="report-chart-empty">No data</div>
                                )}
                              </div>
                            </div>
                            <div className="report-chart-card">
                              <div className="report-chart-title">Average defect types/day ({reportMachine})</div>
                              <div className="report-chart-body">
                                {defectTypePareto.length > 0 ? (
                                  <ResponsiveContainer width="100%" height={240}>
                                    <BarChart data={defectTypePareto} margin={{ top: 10, right: 10, left: -10, bottom: 0 }}>
                                      <CartesianGrid stroke="rgba(148,163,184,0.2)" strokeDasharray="4 4" />
                                      <XAxis dataKey="defect" tick={{ fontSize: 10, fill: "#94a3b8" }} interval={0} />
                                      <YAxis tick={{ fontSize: 10, fill: "#94a3b8" }} />
                                      <Tooltip formatter={(value) => [value, "Avg defects/day"]} />
                                      <Bar dataKey="value" name="Avg defects/day">
                                        {defectTypePareto.map((entry, index) => (
                                          <Cell
                                            key={`machine-defect-type-cell-${index}`}
                                            fill={entry.value === defectTypeParetoMax ? "#ef4444" : "#38bdf8"}
                                          />
                                        ))}
                                      </Bar>
                                    </BarChart>
                                  </ResponsiveContainer>
                                ) : (
                                  <div className="report-chart-empty">No data</div>
                                )}
                              </div>
                            </div>
                          </>
                        )}
                        {reportMachine === "all" && (
                          <div className="report-chart-card">
                            <div className="report-chart-title">PPH by machine</div>
                            <div className="report-chart-body">
                              {pphPareto.length > 0 ? (
                                <ResponsiveContainer width="100%" height={240}>
                                  <BarChart data={pphPareto} margin={{ top: 10, right: 10, left: -10, bottom: 0 }}>
                                    <CartesianGrid stroke="rgba(148,163,184,0.2)" strokeDasharray="4 4" />
                                    <XAxis dataKey="machine" tick={{ fontSize: 10, fill: "#94a3b8" }} interval={0} />
                                    <YAxis tick={{ fontSize: 10, fill: "#94a3b8" }} />
                                    <Tooltip formatter={(value) => [value, "PPH"]} />
                                    <Bar dataKey="value" name="PPH">
                                      {pphPareto.map((entry, index) => (
                                        <Cell
                                          key={`productivity-cell-${index}`}
                                          fill={entry.value === pphParetoMin ? "#ef4444" : "#38bdf8"}
                                        />
                                      ))}
                                    </Bar>
                                  </BarChart>
                                </ResponsiveContainer>
                              ) : (
                                <div className="report-chart-empty">No data</div>
                              )}
                            </div>
                          </div>
                        )}
                        {reportMachine === "all" && (
                          <div className="report-chart-card">
                            <div className="report-chart-title">Average downtime minutes/day by machine</div>
                            <div className="report-chart-body">
                              {downtimePareto.length > 0 ? (
                                <ResponsiveContainer width="100%" height={240}>
                                  <BarChart data={downtimePareto} margin={{ top: 10, right: 10, left: -10, bottom: 0 }}>
                                    <CartesianGrid stroke="rgba(148,163,184,0.2)" strokeDasharray="4 4" />
                                    <XAxis dataKey="machine" tick={{ fontSize: 10, fill: "#94a3b8" }} interval={0} />
                                    <YAxis tick={{ fontSize: 10, fill: "#94a3b8" }} />
                                    <Tooltip formatter={(value) => [value, "Avg downtime min/day"]} />
                                    <Bar dataKey="value" name="Avg downtime min/day">
                                      {downtimePareto.map((entry, index) => (
                                        <Cell
                                          key={`downtime-cell-${index}`}
                                          fill={entry.value === downtimeParetoMax ? "#ef4444" : "#38bdf8"}
                                        />
                                      ))}
                                    </Bar>
                                  </BarChart>
                                </ResponsiveContainer>
                              ) : (
                                <div className="report-chart-empty">No data</div>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                    {reportMachine !== "all" && (
                      <div className="report-hourly-section">
                        <div className="report-pareto-title">Hourly detail ({reportMachine})</div>
                        <div className="report-table-wrap report-hourly-table-wrap">
                          {selectedMachineHourlyRows.length > 0 ? (
                            <table className="report-table report-hourly-table">
                              <thead>
                                <tr>
                                  <th>Hour</th>
                                  <th>Planned units (avg)</th>
                                  <th>Actual units (avg)</th>
                                  <th>Gap (avg)</th>
                                  <th>Defects (avg)</th>
                                  <th>Downtime occurrence (avg)</th>
                                  <th>Downtime minutes (avg)</th>
                                </tr>
                              </thead>
                              <tbody>
                                {selectedMachineHourlyRows.map((row, index) => (
                                  <tr key={`${row.hour_start}-${index}`}>
                                    <td>
                                      {row.hour_start}
                                      {row.hour_end ? ` - ${row.hour_end}` : ""}
                                    </td>
                                    <td>{row.planned_units.toFixed(1)}</td>
                                    <td>{row.actual_units.toFixed(1)}</td>
                                    <td>{row.gap_units.toFixed(1)}</td>
                                    <td>{row.defects_total.toFixed(1)}</td>
                                    <td>{row.downtime_occurrence.toFixed(1)}</td>
                                    <td>{row.downtime_minutes.toFixed(1)}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          ) : (
                            <div className="report-chart-empty">No data</div>
                          )}
                        </div>
                      </div>
                    )}
                  </>
                )}
              </>
            )}
          </div>
        )}
        {activePage === "Factory Floor" && (
          <FactoryFloor1Table
            getDataSourceConfig={getDataSourceConfig}
            buildBlobUrl={buildBlobUrl}
            onAgentDeepDive={(prompt) => {
              setActivePage("Agentic AI");
              handleAgentSubmit(prompt, null, true);
            }}
          />
        )}
        {activePage === "Agentic AI" && (
          <AgentErrorBoundary>
          <div className="card" style={{ 
            height: "calc(100vh - 120px)", 
            display: "flex", 
            flexDirection: "column",
            padding: "0",
            overflow: "hidden"
          }}>
            {/* Page header — full width, sits above the activity rail */}
            <div style={{ padding: "16px 16px 12px", borderBottom: "1px solid var(--border-color, #e5e7eb)", flexShrink: 0 }}>
              <h2 style={{ marginTop: 0, marginBottom: "8px" }}>StratAlign Agentic AI</h2>
              <p style={{ color: "var(--text-muted)", margin: 0 }}>
                Chat with StratAlign Agentic AI for factory insights. Ask questions about your metrics, trends, or problems.
              </p>
            </div>
            {/* Body row: [activity-rail | sidebar | chat] */}
            <div style={{ flex: 1, display: "flex", flexDirection: "row", overflow: "hidden", minHeight: 0 }}>
            {/* Sidebar zone — wraps activity-rail + panel so click-outside handler excludes both */}
            <div ref={sidebarZoneRef} style={{ display: "flex", flexDirection: "row" }}>
            {/* Activity Rail — always-visible left column with icon buttons */}
            <div className="activity-rail">
              <button
                className={`activity-rail__btn${sidebarOpen ? ' activity-rail__btn--active' : ''}`}
                onClick={() => setSidebarOpen(v => !v)}
                title={sidebarOpen ? "Close history" : "History"}
              >
                <svg width="25" height="25" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8"/>
                  <path d="M3 3v5h5"/>
                  <path d="M12 7v5l4 2"/>
                </svg>
              </button>
            </div>
            <ConversationSidebar
              isOpen={sidebarOpen}
              onClose={() => setSidebarOpen(false)}
              activeThreadId={agentThreadId}
              onSelectThread={handleLoadThread}
              onNewThread={() => {
                setAgentMessages([]);
                setAgentThreadId(null);
                setAgentError("");
                localStorage.removeItem('agentMessages');
                localStorage.removeItem('agentThreadId');
              }}
              getToken={getAccessToken}
            />
            </div>{/* end sidebar zone */}
            <div style={{ flex: 1, display: "flex", flexDirection: "column", padding: "16px", minWidth: 0, overflow: "hidden" }}>
            {/* Chat messages display */}
            <div
              ref={chatContainerRef}
              style={{
                border: "1px solid var(--border-color)",
                borderRadius: "8px",
                flex: "1",
                minHeight: 0,
                overflowY: "auto",
                padding: "12px",
                backgroundColor: "var(--bg-light)",
                marginBottom: "12px",
                display: "flex",
                flexDirection: "column",
                scrollBehavior: "smooth"
              }}
            >
              {agentMessages.length === 0 ? (
                <div style={{ 
                  display: "flex", 
                  flexDirection: "column",
                  alignItems: "center", 
                  justifyContent: "center", 
                  height: "100%", 
                  gap: "16px",
                  padding: "20px"
                }}>
                  <p style={{ color: "var(--text-muted)", marginBottom: "8px", fontSize: "1em" }}>
                    How can I help you today?
                  </p>
                  <div style={{ display: "flex", flexDirection: "column", gap: "12px", width: "100%", maxWidth: "500px" }}>
                    <button
                      onClick={() => handleAgentSubmit("Problem Solving")}
                      className="btn"
                      style={{
                        padding: "16px 24px",
                        fontSize: "1em",
                        fontWeight: "500",
                        backgroundColor: "#3b82f6",
                        color: "white",
                        border: "none",
                        borderRadius: "8px",
                        cursor: "pointer",
                        transition: "all 0.2s ease",
                        boxShadow: "0 2px 4px rgba(0,0,0,0.1)"
                      }}
                      onMouseEnter={(e) => {
                        e.target.style.backgroundColor = "#2563eb";
                        e.target.style.transform = "translateY(-2px)";
                        e.target.style.boxShadow = "0 4px 8px rgba(0,0,0,0.15)";
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.backgroundColor = "#3b82f6";
                        e.target.style.transform = "translateY(0)";
                        e.target.style.boxShadow = "0 2px 4px rgba(0,0,0,0.1)";
                      }}
                    >
                      🔧 Problem Solving
                    </button>
                    <button
                      onClick={() => handleAgentSubmit("Lean Coaching & Gemba")}
                      className="btn"
                      style={{
                        padding: "16px 24px",
                        fontSize: "1em",
                        fontWeight: "500",
                        backgroundColor: "#10b981",
                        color: "white",
                        border: "none",
                        borderRadius: "8px",
                        cursor: "pointer",
                        transition: "all 0.2s ease",
                        boxShadow: "0 2px 4px rgba(0,0,0,0.1)"
                      }}
                      onMouseEnter={(e) => {
                        e.target.style.backgroundColor = "#059669";
                        e.target.style.transform = "translateY(-2px)";
                        e.target.style.boxShadow = "0 4px 8px rgba(0,0,0,0.15)";
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.backgroundColor = "#10b981";
                        e.target.style.transform = "translateY(0)";
                        e.target.style.boxShadow = "0 2px 4px rgba(0,0,0,0.1)";
                      }}
                    >
                      📊 Lean Coaching & Gemba
                    </button>
                    <button
                      onClick={() => handleAgentSubmit("Simulations")}
                      className="btn"
                      style={{
                        padding: "16px 24px",
                        fontSize: "1em",
                        fontWeight: "500",
                        backgroundColor: "#8b5cf6",
                        color: "white",
                        border: "none",
                        borderRadius: "8px",
                        cursor: "pointer",
                        transition: "all 0.2s ease",
                        boxShadow: "0 2px 4px rgba(0,0,0,0.1)"
                      }}
                      onMouseEnter={(e) => {
                        e.target.style.backgroundColor = "#7c3aed";
                        e.target.style.transform = "translateY(-2px)";
                        e.target.style.boxShadow = "0 4px 8px rgba(0,0,0,0.15)";
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.backgroundColor = "#8b5cf6";
                        e.target.style.transform = "translateY(0)";
                        e.target.style.boxShadow = "0 2px 4px rgba(0,0,0,0.1)";
                      }}
                    >
                      🎯 Simulations
                    </button>
                  </div>
                </div>
              ) : (
                <>
                  {agentMessages.map((msg, idx) => (
                    (() => {
                      const isUser = msg.role === "user";
                      const raw = msg.content || "";
                      const normalized = raw
                        .replace(/\n{3,}/g, "\n\n")
                        .trim();
                      return (
                    <div
                      key={idx}
                      {...(!isUser && isA3Message(normalized) ? { 'data-a3-message-id': idx } : {})}
                      className={`agent-message-bubble ${msg.role === "user" ? "agent-message-user" : "agent-message-assistant"}`}
                      style={{
                        marginBottom: "8px",
                        padding: msg.role === "user" ? "8px 12px" : "8px 0",
                        borderRadius: msg.role === "user" ? "6px" : "0",
                        backgroundColor: msg.role === "user" ? "#e6f3ff" : "transparent",
                        color: msg.role === "user" ? "#111" : "var(--text-color)",
                        alignSelf: msg.role === "user" ? "flex-end" : "flex-start",
                        maxWidth: msg.role === "user" ? "85%" : "100%",
                        wordWrap: "break-word"
                      }}
                    >
                      <strong style={{ fontSize: "0.85em" }}>
                        {msg.role === "user" ? "You" : (
                          <span style={{ display: "inline-flex", alignItems: "center", gap: "6px" }}>
                            Agent
                            {msg.agent && (
                              <span
                                title={
                                  msg.agent === "problem-solving"      ? "Problem Solving sub-agent"
                                  : msg.agent === "predictive-analytics" ? "Predictive Analytics sub-agent"
                                  : msg.agent === "simulation"            ? "Simulation sub-agent"
                                  : msg.agent === "lean-coaching"         ? "Lean Coaching sub-agent"
                                  : msg.agent
                                }
                                style={{
                                fontSize: "0.72em",
                                fontWeight: 600,
                                padding: "1px 6px",
                                borderRadius: "10px",
                                cursor: "help",
                                backgroundColor: msg.agent === "problem-solving" ? "#dbeafe"
                                               : msg.agent === "predictive-analytics" ? "#fef3c7"
                                               : msg.agent === "simulation" ? "#dcfce7"
                                               : msg.agent === "lean-coaching" ? "#fce7f3"
                                               : "#f1f5f9",
                                color: msg.agent === "problem-solving" ? "#1e40af"
                                     : msg.agent === "predictive-analytics" ? "#92400e"
                                     : msg.agent === "simulation" ? "#166534"
                                     : msg.agent === "lean-coaching" ? "#9d174d"
                                     : "#475569",
                                textTransform: "uppercase",
                                letterSpacing: "0.04em"
                              }}>
                                {msg.agent === "problem-solving"      ? "PS"
                                 : msg.agent === "predictive-analytics" ? "PA"
                                 : msg.agent === "simulation"            ? "SIM"
                                 : msg.agent === "lean-coaching"         ? "LC"
                                 : msg.agent.slice(0, 3).toUpperCase()}
                              </span>
                            )}
                          </span>
                        )}:
                      </strong>
                      {isUser ? (
                        <p style={{ margin: "4px 0 0 0", lineHeight: "1.4", whiteSpace: "pre-wrap" }}>{msg.content}</p>
                      ) : (
                        <EnhancedMarkdownRenderer content={normalized} charts={msg.charts || []} />
                      )}
                      {!isUser && isA3Message(normalized) && (
                        <div style={{ marginTop: '14px', paddingTop: '10px', borderTop: '1px solid #e5e7eb' }}>
                          <button
                            className="a3-download-btn"
                            disabled={generatingA3Idx === idx}
                            onClick={() => handleDownloadA3Pdf(normalized, idx)}
                          >
                            {generatingA3Idx === idx
                              ? '⏳ Generating PDF...'
                              : '⬇ Download A3 as PDF'}
                          </button>
                        </div>
                      )}
                    </div>
                      );
                    })()
                  ))}
                  {/* Animated loading indicator */}
                  {agentLoading && (
                    <div style={{ marginTop: "12px", maxWidth: "85%" }}>
                      <div style={{ 
                        color: "var(--text-muted)", 
                        fontStyle: "italic",
                        display: "flex",
                        alignItems: "center",
                        gap: "8px",
                        marginBottom: "8px"
                      }}>
                        <span style={{
                          display: "inline-block",
                          width: "6px",
                          height: "6px",
                          borderRadius: "50%",
                          backgroundColor: "#3b82f6",
                          animation: "pulse 1.5s infinite"
                        }} />
                        <span 
                          className="streaming-text"
                          style={{
                            transition: "opacity 0.3s ease-in-out"
                          }}
                        >
                          {loadingMessages[loadingMessageIndex].text}
                        </span>
                      </div>
                      {/* Streaming progress bar */}
                      <div style={{
                        width: "100%",
                        height: "3px",
                        backgroundColor: "rgba(59, 130, 246, 0.3)",
                        borderRadius: "2px",
                        overflow: "hidden",
                        position: "relative"
                      }}>
                        <div style={{
                          position: "absolute",
                          top: 0,
                          left: "-100%",
                          width: "100%",
                          height: "100%",
                          background: "linear-gradient(90deg, transparent 0%, rgba(255, 255, 255, 0.6) 50%, transparent 100%)",
                          animation: "shimmer 1.5s linear infinite",
                          willChange: "transform"
                        }} />
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>

            {/* Error display */}
            {agentError && (
              <div
                style={{
                  padding: "10px 14px",
                  backgroundColor: "#fee",
                  border: "1px solid #fcc",
                  borderRadius: "6px",
                  color: "#c00",
                  marginBottom: "12px",
                  fontSize: "0.9em"
                }}
              >
                {agentError}
              </div>
            )}

            {/* Input area */}
            <div style={{ display: "flex", justifyContent: "center", width: "100%" }}>
              <div style={{ display: "flex", flexDirection: "column", gap: "10px", width: "90%", maxWidth: "900px" }}>
                <textarea
                  ref={textareaRef}
                  placeholder="Type your question...&#10;Use Shift+Enter for new line"
                  value={agentInput}
                  onChange={(e) => {
                    setAgentInput(e.target.value);
                    // Auto-resize textarea
                    e.target.style.height = "auto";
                    e.target.style.height = e.target.scrollHeight + "px";
                  }}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && !e.shiftKey && !agentLoading) {
                      e.preventDefault();
                      handleAgentSubmit();
                    }
                  }}
                  disabled={agentLoading}
                  style={{
                    width: "100%",
                    padding: "10px 14px",
                    border: "2px solid #333",
                    borderRadius: "6px",
                    fontFamily: "inherit",
                    fontSize: "0.95em",
                    opacity: agentLoading ? 0.6 : 1,
                    outline: "none",
                    resize: "none",
                    minHeight: "60px",
                    maxHeight: "200px",
                    overflowY: "auto",
                    boxSizing: "border-box"
                  }}
                />
                <div style={{ display: "flex", gap: "10px", justifyContent: "center", flexWrap: "wrap" }}>
                  <button
                    onClick={() => handleAgentSubmit()}
                    disabled={agentLoading || !agentInput.trim()}
                    className="btn btn-primary"
                    style={{ 
                      padding: "10px 24px", 
                      height: "42px",
                      minWidth: "100px",
                      fontSize: "0.95em",
                      fontWeight: "500"
                    }}
                  >
                    Send
                  </button>
                  <button
                    onClick={() => {
                      // Do NOT delete the backend thread — storeConversation() already saved it
                      // to Cosmos on every turn. Deleting it would remove it from the History
                      // sidebar permanently. Instead, just reset local UI state so the next
                      // message starts a fresh thread while the old one remains in History.
                      setAgentMessages([]);
                      setAgentError("");
                      setAgentThreadId(null);
                      localStorage.removeItem('agentMessages');
                      localStorage.removeItem('agentThreadId');
                      console.log('[Clear] Reset local state — thread preserved in Cosmos history');
                    }}
                  className="btn"
                  style={{ 
                    padding: "10px 24px", 
                    height: "42px",
                    minWidth: "100px",
                    fontSize: "0.95em",
                    fontWeight: "500"
                  }}
                >
                  Clear
                  </button>
                </div>
              </div>
            </div>
            </div>
            </div>{/* end body row */}
          </div>
          </AgentErrorBoundary>
        )}
        {activePage === "A3 & Kaizen" && (
          <div className="card">
            <div className="report-header">
              <div>
                <h2 className="report-title">A3 & Kaizen</h2>
                <p className="report-subtitle">Continuous improvement tracking</p>
              </div>
              <div className="report-meta">
                <span>{filteredA3Records.length} items</span>
              </div>
            </div>
            <div className="report-controls">
              <label className="report-control">
                <span>Value stream</span>
                <select
                  className="report-input"
                  value={a3ValueStream}
                  onChange={(event) => setA3ValueStream(event.target.value)}
                >
                  <option value="all">All value streams</option>
                  {a3ValueStreamOptions.map((stream) => (
                    <option key={stream} value={stream}>
                      {stream}
                    </option>
                  ))}
                </select>
              </label>
              <label className="report-control">
                <span>Department</span>
                <select
                  className="report-input"
                  value={a3Department}
                  onChange={(event) => setA3Department(event.target.value)}
                >
                  <option value="all">All departments</option>
                  {a3DepartmentOptions.map((department) => (
                    <option key={department} value={department}>
                      {department}
                    </option>
                  ))}
                </select>
              </label>
            </div>
            <div className="report-table-wrap">
              {filteredA3Records.length > 0 ? (
                <table className="report-table">
                  <thead>
                    <tr>
                      <th>A3 details</th>
                      <th>Owner</th>
                      <th>Status</th>
                      <th>Expected output</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredA3Records.map((record, index) => (
                      <tr key={`${record.a3Details}-${index}`}>
                        <td>{record.a3Details}</td>
                        <td>{record.owner}</td>
                        <td>{record.status}</td>
                        <td>{record.expectedOutput}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <div className="report-chart-empty">No items match the filters</div>
              )}
            </div>
          </div>
        )}
      </main>
    </div>
  );
}

export default App;
